/* Meeting Mode — rooms, live calls, shared notes.
   ================================================

   NO onReady() WRAPPER HERE, deliberately. This one already gates itself: the
   last two lines call wire() on DOMContentLoaded, or immediately if the
   document is already parsed — which is exactly what onReady does. Adding a
   second gate on top would delay the globals it publishes (otterAI among
   them) for no reason.

   It also looks elements up lazily through overlay() and el() rather than
   caching them at load, which is why it survives being moved at all.

   Module top-level code runs on import, so the behaviour is unchanged: the
   bundle imports this, the assignments happen, and wire() waits for the DOM.
*/

  'use strict';

  const MM_BGS = window.MT_BACKGROUNDS = [
    'backgrounds/meeting-bg.jpg', 'backgrounds/meeting-bg2.jpg', 'backgrounds/meeting-bg3.jpg',
    'backgrounds/meeting-bg4.jpg', 'backgrounds/meeting-bg5.jpg', 'backgrounds/meeting-bg6.jpg',
    'backgrounds/meeting-bg7.jpg', 'backgrounds/meeting-bg8.jpg'
  ];
  const MM_BG_FALLBACK = 'linear-gradient(160deg,#3f6f9e 0%,#6f97bb 40%,#efb886 82%,#c99f86 100%)';

  /* Background warming.

     The photo used to be requested at the moment Meeting Mode opened, so you
     watched half a megabyte arrive over the gradient. Instead one background
     is chosen ahead of time and fetched while the browser is idle, so by the
     time anyone clicks it is already in cache and paints instantly. After each
     open the next one is chosen and warmed the same way — only ever one image
     in flight, never all eight. */
  let mmNextBg = null, mmLastBg = null;
  /* Pick a background that isn't the one just used. With eight photos, plain
     random repeats often enough to look like a bug — you close Meeting Mode,
     reopen it, and get the same beach. */
  function mmPickBg() {
    const pool = MM_BGS.length > 1 ? MM_BGS.filter(b => b !== mmLastBg) : MM_BGS;
    return pool[Math.floor(Math.random() * pool.length)];
  }
  function mmWarmNextBg() {
    const pick = mmPickBg();
    const img = new Image();
    img.onload = () => { mmNextBg = pick; };
    img.onerror = () => { mmNextBg = null; };   // fall back to the gradient
    img.src = pick;
  }
  /* Warm the first one once the page has settled, so it never competes with
     the app's own startup work.

     requestIdleCallback must be invoked ON window — pulling the reference out
     and calling it bare throws "Illegal invocation" in Chrome, because native
     browser APIs check their receiver. Hence the explicit window. calls rather
     than the shorter (a || b)(...) form. */
  function mmWarmSoon() {
    try {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(mmWarmNextBg, { timeout: 3000 });
      } else {
        window.setTimeout(mmWarmNextBg, 1200);
      }
    } catch (e) {
      // Warming is an optimisation; never let it break the app.
      try { window.setTimeout(mmWarmNextBg, 1200); } catch (e2) {}
    }
  }
  if (document.readyState === 'complete') mmWarmSoon();
  else window.addEventListener('load', mmWarmSoon, { once: true });
  const MM_TEAM_ROOM = 'team';

  let mmView = 'lobby';
  let mmEditingId = null;
  let mmEditPublic = false;
  let mmClient = null;
  let mmLastClient = null;
  let mmCalAnchor = new Date();
  let mmCalSelected = null;
  let mmSessionNoteIds = [];
  // Rooms
  let mmRoomId = null, mmRoomType = null, mmRoomOwnerUid = null, mmRoomName = '';
  let mmRoomViewUid = null, mmRoomPending = false;
  let mmRoomMembers = [], mmRoomNotes = [], mmRoomRequests = [];
  let mmRoomUnsubs = {};
  let mmLobbyTimer = null;
  let mmLocalNotes = [];
  // Always-on listener for people asking to join MY private room
  let mmMyReqUnsub = null;
  let mmMyRequests = [];

  const el = id => document.getElementById(id);
  const overlay = () => el('meeting-mode');

  function escHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
  function calTodayKeySafe() { return (typeof calTodayKey === 'function') ? calTodayKey() : calDateKeySafe(new Date()); }
  function calDateKeySafe(d) {
    return (typeof calDateKey === 'function') ? calDateKey(d)
      : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  function mmParseClients(j) { try { const a = JSON.parse(j || '[]'); return Array.isArray(a) ? a : []; } catch (e) { return []; } }
  function mmTimeLabel(iso) { if (!iso) return ''; const d = new Date(iso); if (isNaN(d.getTime())) return ''; return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }); }
  function mmInitials(name) { return (name || '?').trim().split(/\s+/).map(w => w[0] || '').slice(0, 2).join('').toUpperCase() || '?'; }

  // ---------- room plumbing ----------
  function mmTeam() { return !!(typeof notesTeamMode === 'function' && notesTeamMode()); }
  function mmUid() { return (typeof currentUser !== 'undefined' && currentUser) ? currentUser.uid : null; }
  function mmMyName() {
    try { if (typeof contactAuthorName === 'function') { const n = contactAuthorName(); if (n) return n; } } catch (e) {}
    return (currentUser && (currentUser.displayName || currentUser.email)) || 'Member';
  }
  function mmRoomRef(id) { return fbDb.collection('teams').doc(activeTeamId).collection('rooms').doc(id); }
  function mmPrivateId(uid) { return 'p_' + (uid || mmUid()); }
  function memberFresh(m) { if (!m || !m.meetingSince) return true; const t = new Date(m.meetingSince).getTime(); return isNaN(t) || (Date.now() - t) < 6 * 3600 * 1000; }

  function mmLocalKey() { return 'mt.meetingNotes.' + (activeTeamId || 'personal'); }
  function mmLoadLocal() { try { const v = JSON.parse(localStorage.getItem(mmLocalKey()) || '[]'); return Array.isArray(v) ? v : []; } catch (e) { return []; } }
  function mmSaveLocal() { try { localStorage.setItem(mmLocalKey(), JSON.stringify(mmLocalNotes)); } catch (e) {} }

  function mmAllNotes() { return mmTeam() ? mmRoomNotes : mmLocalNotes; }
  function mmNotesForUid(uid) {
    const mine = uid === mmUid();
    return mmAllNotes().filter(n => n.authorUid === uid && (mine || n.public === true))
      .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
  }

  async function mmNoteAdd(o) {
    const now = new Date().toISOString();
    const doc = { title: o.title || '', body: o.body || '', clientsJson: o.clientsJson || '[]', public: !!o.isPublic, color: o.color || '', authorUid: mmUid(), authorName: mmMyName(), createdAt: now, updatedAt: now };
    if (mmTeam() && mmRoomId) { const ref = await mmRoomRef(mmRoomId).collection('notes').add(doc); return ref.id; }
    const id = 'rn' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    mmLocalNotes.unshift({ id, ...doc }); mmSaveLocal(); mmRenderRoom(); return id;
  }
  async function mmNoteUpdate(id, patch) {
    patch.updatedAt = new Date().toISOString();
    if (mmTeam() && mmRoomId) { await mmRoomRef(mmRoomId).collection('notes').doc(id).update(patch); return; }
    const n = mmLocalNotes.find(x => x.id === id); if (n) { Object.assign(n, patch); mmSaveLocal(); mmRenderRoom(); }
  }
  async function mmNoteDelete(id) {
    if (mmTeam() && mmRoomId) { await mmRoomRef(mmRoomId).collection('notes').doc(id).delete(); return; }
    mmLocalNotes = mmLocalNotes.filter(x => x.id !== id); mmSaveLocal(); mmRenderRoom();
  }
  async function mmToggleNotePublic(id) {
    const n = mmAllNotes().find(x => x.id === id); if (!n) return;
    try { await mmNoteUpdate(id, { public: !(n.public === true) }); } catch (e) { mmToast('Could not change sharing', true); }
  }

  async function mmCopyNoteToNotes(n, folder) {
    const now = new Date().toISOString();
    // If a client is attached, link it properly and file under the client's folder.
    const r = (typeof notesResolveAttached === 'function') ? notesResolveAttached(n.clientsJson, folder) : { clientsJson: n.clientsJson || '[]', folder: folder || '' };
    const base = { title: n.title || '', body: n.body || '', kind: 'note', folder: r.folder || '', clientsJson: r.clientsJson, clientAssignedUids: (typeof notesClientAccessUids === 'function' ? notesClientAccessUids(r.clientsJson) : []), color: n.color || '', shareAll: !!n.public, sharedWithJson: '[]', sharedWith: [], createdAt: now, updatedAt: now };
    if (mmTeam()) {
      const doc = { ...base, addedBy: mmUid(), addedByName: mmMyName() };
      const ref = await fbDb.collection('teams').doc(activeTeamId).collection('notes').add(doc);
      if (typeof teamNotesCache !== 'undefined' && !teamNotesCache.find(x => x.id === ref.id)) teamNotesCache.push({ id: ref.id, ...doc });
    } else {
      const data = notesLoadPersonal();
      const nid = 'nt' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      data.notes.push({ ...base, id: nid }); notesSavePersonal(data);
    }
    await mmNoteUpdate(n.id, { addedToNotes: true });
  }
  async function mmAddNoteToNotes(id) {
    const n = mmAllNotes().find(x => x.id === id); if (!n) return;
    try { await mmCopyNoteToNotes(n); if (typeof renderNotes === 'function') { try { renderNotes(); } catch (e) {} } mmToast('Added to Notes ✓'); }
    catch (e) { mmToast('Could not add to Notes', true); }
  }
  async function mmAddAllToNotes() {
    const notes = mmNotesForUid(mmUid()).filter(n => !n.addedToNotes);
    if (!notes.length) { mmToast('Nothing new to add', true); return; }
    try { for (const n of notes) await mmCopyNoteToNotes(n); if (typeof renderNotes === 'function') { try { renderNotes(); } catch (e) {} } mmToast('Added ' + notes.length + ' to Notes ✓'); }
    catch (e) { mmToast('Could not add all', true); }
  }

  function mmClearRoomListeners() { Object.values(mmRoomUnsubs).forEach(u => { try { u && u(); } catch (e) {} }); mmRoomUnsubs = {}; }
  async function mmEnterRoom(roomId, type, ownerUid, name) {
    mmClearRoomListeners();
    mmRoomId = roomId; mmRoomType = type; mmRoomOwnerUid = ownerUid || null; mmRoomName = name; mmRoomViewUid = mmUid(); mmRoomPending = false;
    mmRoomMembers = []; mmRoomNotes = []; mmRoomRequests = [];
    if (mmTeam()) {
      const now = new Date().toISOString();
      try {
        await mmRoomRef(roomId).set({ name, type, ownerUid: ownerUid || null, updatedAt: now }, { merge: true });
        await mmRoomRef(roomId).collection('members').doc(mmUid()).set({ name: mmMyName(), active: true, approved: true, joinedAt: now, updatedAt: now }, { merge: true });
      } catch (e) {}
      mmWireRoom();
    } else {
      mmLocalNotes = mmLoadLocal();
    }
    mmSetPresence(true, roomId, name);
    mmSetView('room');
  }
  async function mmRequestJoin(ownerUid, ownerName) {
    if (!mmTeam()) return;
    mmClearRoomListeners();
    const roomId = mmPrivateId(ownerUid);
    mmRoomId = roomId; mmRoomType = 'private'; mmRoomOwnerUid = ownerUid; mmRoomName = (ownerName || 'Private') + "'s room"; mmRoomViewUid = mmUid(); mmRoomPending = true;
    mmRoomMembers = []; mmRoomNotes = []; mmRoomRequests = [];
    try { await mmRoomRef(roomId).collection('requests').doc(mmUid()).set({ name: mmMyName(), requestedAt: new Date().toISOString() }); } catch (e) {}
    mmWireRoom();
    mmSetView('room');
  }
  function mmWireRoom() {
    if (!mmTeam() || !mmRoomId) return;
    const ref = mmRoomRef(mmRoomId);
    mmRoomUnsubs.members = ref.collection('members').onSnapshot(s => {
      mmRoomMembers = s.docs.map(d => ({ uid: d.id, ...d.data() }));
      if (mmRoomPending) {
        const me = mmRoomMembers.find(m => m.uid === mmUid());
        if (me && me.approved) { mmRoomPending = false; ref.collection('members').doc(mmUid()).set({ active: true, updatedAt: new Date().toISOString() }, { merge: true }); }
      }
      mmRenderRoom();
    }, e => { console.warn('room members listener rejected:', e && (e.code || e.message)); });
    /* Since private rooms became rules-enforced, this listener is REJECTED for
       someone who has knocked but not been approved — which is correct, but it
       arrives as a rejected listener rather than a thrown error. Without this
       warning, "waiting to be let in" and "the rules are wrong" look identical
       from the outside, and that has already cost us a long afternoon twice. */
    mmRoomUnsubs.notes = ref.collection('notes').onSnapshot(
      s => { mmRoomNotes = s.docs.map(d => ({ id: d.id, ...d.data() })); mmRenderRoom(); },
      e => {
        console.warn('room notes listener rejected:', e && (e.code || e.message),
          mmRoomPending ? '(expected — still waiting for approval)' : '(NOT expected — check the rules)');
        mmRoomNotes = [];
        mmRenderRoom();
      });
    // Join requests are handled by the always-on global listener (mmStartMyReqListener),
    // so the owner is notified in any view, not only while sitting in their room.
  }

  // ----- Always-on "someone wants to join my private room" notifications -----
  function mmStartMyReqListener() {
    if (!mmTeam()) return;
    mmStopMyReqListener();
    try {
      mmMyReqUnsub = mmRoomRef(mmPrivateId(mmUid())).collection('requests').onSnapshot(s => {
        mmMyRequests = s.docs.map(d => ({ uid: d.id, ...d.data() }));
        mmRenderGlobalReq();
      }, e => {});
    } catch (e) {}
  }
  function mmStopMyReqListener() {
    if (mmMyReqUnsub) { try { mmMyReqUnsub(); } catch (e) {} mmMyReqUnsub = null; }
    mmMyRequests = [];
    mmRenderGlobalReq();
  }
  function mmRenderGlobalReq() {
    const b = el('mm-global-req'); if (!b) return;
    if (!mmMyRequests.length) { b.style.display = 'none'; b.innerHTML = ''; return; }
    b.innerHTML = mmMyRequests.map(r => `<div class="mm-req"><span><b>${escHtml(r.name || 'Someone')}</b> wants to join your room</span><span class="mm-req-btns"><button class="mm-req-yes" data-uid="${escHtml(r.uid)}" data-name="${escHtml(r.name || '')}">Let in</button><button class="mm-req-no" data-uid="${escHtml(r.uid)}">Dismiss</button></span></div>`).join('');
    b.style.display = 'block';
    b.querySelectorAll('.mm-req-yes').forEach(x => x.addEventListener('click', () => mmApproveGlobal(x.dataset.uid, x.dataset.name)));
    b.querySelectorAll('.mm-req-no').forEach(x => x.addEventListener('click', () => mmDenyGlobal(x.dataset.uid)));
  }
  async function mmApproveGlobal(uid, name) {
    const rid = mmPrivateId(mmUid());
    const ref = mmRoomRef(rid);
    const now = new Date().toISOString();
    try {
      await ref.set({ name: 'Your private room', type: 'private', ownerUid: mmUid(), updatedAt: now }, { merge: true });
      await ref.collection('members').doc(uid).set({ name: name || 'Member', approved: true, active: false, joinedAt: now, updatedAt: now }, { merge: true });
      await ref.collection('requests').doc(uid).delete();
      mmToast('Let ' + (name || 'them') + ' in ✓');
    } catch (e) { mmToast('Could not approve', true); }
  }
  async function mmDenyGlobal(uid) { try { await mmRoomRef(mmPrivateId(mmUid())).collection('requests').doc(uid).delete(); } catch (e) {} }
  async function mmApprove(uid, name) {
    const ref = mmRoomRef(mmRoomId); const now = new Date().toISOString();
    try { await ref.collection('members').doc(uid).set({ name, approved: true, active: false, joinedAt: now, updatedAt: now }, { merge: true }); await ref.collection('requests').doc(uid).delete(); } catch (e) {}
  }
  async function mmDeny(uid) { try { await mmRoomRef(mmRoomId).collection('requests').doc(uid).delete(); } catch (e) {} }
  async function mmLeaveRoom() {
    if (mmTeam() && mmRoomId) {
      const now = new Date().toISOString();
      try { await mmRoomRef(mmRoomId).collection('members').doc(mmUid()).set({ active: false, updatedAt: now }, { merge: true }); } catch (e) {}
      if (mmRoomPending) { try { await mmRoomRef(mmRoomId).collection('requests').doc(mmUid()).delete(); } catch (e) {} }
    }
    mmClearRoomListeners();
    mmRoomMembers = []; mmRoomNotes = []; mmRoomRequests = [];
    mmRoomId = null; mmRoomPending = false;
    mmSetPresence(false);
  }

  // ---------- lobby ----------
  function mmActivePrivateHosts() {
    if (!mmTeam()) return [];
    const me = mmUid();
    return (typeof teamMembersCache !== 'undefined' ? teamMembersCache : [])
      .filter(m => m.uid !== me && m.meetingActive === true && m.meetingRoomId === mmPrivateId(m.uid) && memberFresh(m));
  }
  function mmTeamRoomPeople() {
    if (!mmTeam()) return [];
    return (typeof teamMembersCache !== 'undefined' ? teamMembersCache : [])
      .filter(m => m.meetingActive === true && m.meetingRoomId === MM_TEAM_ROOM && memberFresh(m));
  }
  function mmTeamRoomCount() { return mmTeamRoomPeople().length; }

  /* Face pile for the lobby cards. Everything here comes from the team member
     documents that are already synced for the People tab, so showing who is in
     a room costs no extra reads and never touches LiveKit. */
  function mmFacePile(people, max) {
    if (!people.length) return '';
    const cap = max || 5;
    const shown = people.slice(0, cap);
    const rest = people.length - shown.length;
    const chips = shown.map(m => {
      const first = (m.name || 'Member').split(' ')[0];
      const you = m.uid === mmUid() ? ' (you)' : '';
      return `<span class="mm-face" title="${escHtml(m.name || 'Member')}">` +
             `<span class="mm-face-av">${escHtml(mmInitials(m.name))}</span>` +
             `<span>${escHtml(first)}${you}</span></span>`;
    }).join('');
    return `<div class="mm-faces">${chips}${rest > 0 ? `<span class="mm-face mm-face-more">+${rest}</span>` : ''}</div>`;
  }

  /* ---------------------------------------------------------------------
     "My items" — a reference panel for the person on the call.

     Reads ONLY from the in-memory caches, which the scoped listeners have
     already filtered to this user's own records. That means: no extra reads,
     no cost, no way to show a colleague's data, and nothing to change in the
     security rules. An admin sees more here simply because their listeners
     are broader — the same rules decide it.
  --------------------------------------------------------------------- */
  let mmItemsTab = 'clients';
  const MM_ITEM_TABS = [
    ['clients',   'Clients'],
    ['referrals', 'Referrals'],
    ['calendar',  'Calendar'],
    ['mileage',   'Routes'],
    ['hours',     'Hours'],
    ['approvals', 'Approvals']
  ];

  let mmItemsQuery = '';
  const MM_ITEM_SEARCHABLE = { clients: true, referrals: true };

  function mmItemsData(tab) {
    const arr = x => (typeof x !== 'undefined' && Array.isArray(x)) ? x.slice() : [];
    const byDateDesc = k => (a, b) => String(b[k] || '').localeCompare(String(a[k] || ''));
    if (tab === 'clients')   return arr(teamClientsCache).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (tab === 'referrals') return arr(teamReferralsCache).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (tab === 'mileage')   return arr(teamMileageCache).sort(byDateDesc('date'));
    if (tab === 'hours')     return arr(teamMemberHoursCache).sort(byDateDesc('date'));
    if (tab === 'approvals') return arr(teamApprovalsCache).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (tab === 'calendar') {
      // Upcoming only, and never the colour-topic definitions.
      const today = todayISO();
      return arr(teamEventsCache)
        .filter(e => e.type !== '__topic__' && e.date && e.date >= today)
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));
    }
    return [];
  }

  function mmItemRow(tab, it) {
    const esc = escHtml;
    const when = d => d ? (typeof prettyDate === 'function' ? prettyDate(d) : d) : '';
    if (tab === 'clients') {
      const bits = [];
      if (it.phone) bits.push(esc(it.phone));
      if (it.expirationDate) bits.push('Renews ' + when(it.expirationDate));
      if (it.lastContactedAt) bits.push('Last contact ' + when(it.lastContactedAt));
      return `<div class="mm-item"><div class="mm-item-top"><span class="mm-item-name">${esc(it.name || 'Client')}</span>` +
             `<span class="mm-item-when">${it.cin ? 'CIN ' + esc(it.cin) : ''}</span></div>` +
             (bits.length ? `<div class="mm-item-sub">${bits.join(' · ')}</div>` : '') + `</div>`;
    }
    if (tab === 'referrals') {
      const status = it.converted === 'yes' ? 'Converted' : it.converted === 'no' ? 'Not converted' : 'Pending';
      const bits = [];
      if (it.phone) bits.push(esc(it.phone));
      if (it.referredBy) bits.push('From ' + esc(it.referredBy));
      if (it.dateReceived) bits.push('Received ' + when(it.dateReceived));
      return `<div class="mm-item"><div class="mm-item-top"><span class="mm-item-name">${esc(it.name || 'Referral')}` +
             `<span class="mm-item-pill">${status}</span></span></div>` +
             (bits.length ? `<div class="mm-item-sub">${bits.join(' · ')}</div>` : '') + `</div>`;
    }
    if (tab === 'calendar') {
      const kind = it.type === 'followup' ? 'Follow-up' : 'Event';
      return `<div class="mm-item"><div class="mm-item-top"><span class="mm-item-name">${esc(it.title || kind)}` +
             `<span class="mm-item-pill">${kind}</span></span>` +
             `<span class="mm-item-when">${when(it.date)}${it.time ? ' · ' + esc(it.time) : ''}</span></div>` +
             (it.notes ? `<div class="mm-item-sub">${esc(String(it.notes).slice(0, 140))}</div>` : '') + `</div>`;
    }
    if (tab === 'mileage') {
      let stops = [];
      try { stops = JSON.parse(it.stopsJson || '[]'); } catch (e) {}
      const labels = stops.map(s => s.label || s.name || s.address || '').filter(Boolean);
      const miles = (typeof it.totalMiles === 'number') ? it.totalMiles.toFixed(1) : (it.totalMiles || '0');
      // Clickable: opens the existing route report, map and all. That modal is
      // z-index 9000 against Meeting Mode's 8000, so it layers on top cleanly
      // and there is no need for a second map implementation in here.
      return `<div class="mm-item mm-item-open" data-route="${esc(it.id)}" title="Open the full route with map"><div class="mm-item-top">` +
             `<span class="mm-item-name">${miles} mi · ${stops.length} stop${stops.length === 1 ? '' : 's'}</span>` +
             `<span class="mm-item-when">${when(it.date)} &rsaquo;</span></div>` +
             (labels.length ? `<div class="mm-item-sub">${esc(labels.join('  →  ').slice(0, 160))}</div>` : '') + `</div>`;
    }
    if (tab === 'hours') {
      const mins = it.totalMinutes || 0;
      const hrs = Math.floor(mins / 60), rem = mins % 60;
      const dur = (hrs ? hrs + 'h ' : '') + (rem ? rem + 'm' : (hrs ? '' : '0m'));
      const bits = [];
      if (it.startTime || it.endTime) bits.push(esc((it.startTime || '?') + '–' + (it.endTime || '?')));
      if (it.units) bits.push(it.units + ' unit' + (it.units === 1 ? '' : 's'));
      if (it.newMember) bits.push('New member');
      return `<div class="mm-item"><div class="mm-item-top">` +
             `<span class="mm-item-name">${esc(it.name || 'Session')} · ${dur}</span>` +
             `<span class="mm-item-when">${when(it.date)}</span></div>` +
             (bits.length ? `<div class="mm-item-sub">${bits.join(' · ')}</div>` : '') + `</div>`;
    }
    if (tab === 'approvals') {
      const entries = Array.isArray(it.entries) ? it.entries : [];
      const preview = entries.slice(0, 4).map(e => esc(e.clientName || '') + (e.date ? ' (' + when(e.date) + ')' : '')).filter(Boolean);
      const more = entries.length - preview.length;
      return `<div class="mm-item"><div class="mm-item-top"><span class="mm-item-name">${esc(it.name || 'Approval list')}</span>` +
             `<span class="mm-item-when">${entries.length} entr${entries.length === 1 ? 'y' : 'ies'}</span></div>` +
             (preview.length ? `<div class="mm-item-sub">${preview.join(' · ')}${more > 0 ? ' · +' + more + ' more' : ''}</div>` : '') + `</div>`;
    }
    return '';
  }

  /* Only the LIST is redrawn while typing. Rebuilding the search input itself
     on every keystroke would blur it after the first character. */
  function mmRenderItemsList() {
    const listEl = el('mm-items-list');
    if (!listEl) return;
    const all = mmItemsData(mmItemsTab);
    const q = MM_ITEM_SEARCHABLE[mmItemsTab] ? mmItemsQuery.trim().toLowerCase() : '';
    const rows = q
      ? all.filter(r => ((r.name || '') + ' ' + (r.phone || '')).toLowerCase().includes(q))
      : all;

    if (!rows.length) {
      const blank = q ? `No matches for “${escHtml(mmItemsQuery.trim())}”.` : {
        clients:   'No clients assigned to you yet.',
        referrals: 'No referrals assigned to you yet.',
        calendar:  'Nothing coming up.',
        mileage:   'No routes saved yet.',
        hours:     'No sessions logged yet.',
        approvals: 'No approval lists yet.'
      }[mmItemsTab] || 'Nothing here yet.';
      listEl.innerHTML = `<div class="mm-items-empty">${blank}` +
        (q ? '' : `<br /><span style="font-size:12px; opacity:.8">This panel only ever shows your own records.</span>`) + `</div>`;
      return;
    }
    listEl.innerHTML = rows.slice(0, 200).map(it => mmItemRow(mmItemsTab, it)).join('') +
      (rows.length > 200 ? `<div class="mm-items-count">Showing 200 of ${rows.length}</div>` : '');

    // Routes open the full report with the map.
    listEl.querySelectorAll('[data-route]').forEach(r => r.addEventListener('click', () => {
      const id = r.getAttribute('data-route');
      if (typeof openTeamMileageDetail === 'function') openTeamMileageDetail(id);
    }));
  }

  function mmRenderItems() {
    const tabsEl = el('mm-items-tabs'), listEl = el('mm-items-list');
    if (!tabsEl || !listEl) return;
    tabsEl.innerHTML = MM_ITEM_TABS.map(([k, label]) => {
      const n = mmItemsData(k).length;
      return `<button data-itab="${k}" class="${mmItemsTab === k ? 'active' : ''}">${label}${n ? ' ' + n : ''}</button>`;
    }).join('');
    tabsEl.querySelectorAll('[data-itab]').forEach(b => b.addEventListener('click', () => {
      mmItemsTab = b.dataset.itab;
      mmItemsQuery = '';                       // a fresh tab starts unfiltered
      const s = el('mm-items-search'); if (s) s.value = '';
      mmRenderItems();
    }));

    // Search only makes sense where there are names to search.
    const search = el('mm-items-search');
    if (search) {
      search.hidden = !MM_ITEM_SEARCHABLE[mmItemsTab];
      search.placeholder = mmItemsTab === 'referrals'
        ? 'Search referrals by name or phone…'
        : 'Search clients by name or phone…';
      if (!search.dataset.wired) {
        search.dataset.wired = '1';
        search.addEventListener('input', () => { mmItemsQuery = search.value; mmRenderItemsList(); });
      }
    }
    mmRenderItemsList();
  }

  function mmRenderLobby() {
    const wrap = el('mm-lobby'); if (!wrap) return;
    const team = mmTeam();
    let html = `
      <div class="mm-room-card" id="mm-card-private">
        <div class="mm-room-h">Private room</div>
        <div class="mm-room-d">Just you. Others can ask to join, and you decide who's in.</div>
        <div class="mm-room-go">Enter &rarr;</div>
      </div>`;
    if (team) {
      const inTeamRoom = mmTeamRoomPeople();
      const c = inTeamRoom.length;
      html += `
      <div class="mm-room-card" id="mm-card-team">
        <div class="mm-room-h">Team room ${c ? `<span class="mm-room-count">${c} in</span>` : ''}</div>
        <div class="mm-room-d">${c ? 'Open to everyone. Jump in.' : "Open to everyone. See who's here and share notes you mark public."}</div>
        ${mmFacePile(inTeamRoom)}
        <div class="mm-room-go">${c ? 'Join them' : 'Join'} &rarr;</div>
      </div>`;
      const hosts = mmActivePrivateHosts();
      if (hosts.length) {
        html += `<div class="mm-lobby-sec">Active private rooms</div>`;
        html += hosts.map(h => `<div class="mm-room-card mm-room-req" data-host="${escHtml(h.uid)}" data-name="${escHtml(h.name || 'Member')}">
          <div class="mm-room-h">${escHtml(h.name || 'Member')}</div>
          <div class="mm-room-d">In their private room</div>
          <div class="mm-room-go">Request to join &rarr;</div>
        </div>`).join('');
      }
    } else {
      html += `<div class="mm-lobby-note">Join a team to use shared rooms. Notes here stay on this device until you add them to Notes.</div>`;
    }
    wrap.innerHTML = html;
    const pv = el('mm-card-private'); if (pv) pv.addEventListener('click', () => mmEnterRoom(team ? mmPrivateId() : 'local', 'private', mmUid(), 'Your private room'));
    const tm = el('mm-card-team'); if (tm) tm.addEventListener('click', () => mmEnterRoom(MM_TEAM_ROOM, 'public', null, 'Team room'));
    wrap.querySelectorAll('.mm-room-req').forEach(c => c.addEventListener('click', () => mmRequestJoin(c.dataset.host, c.dataset.name)));
  }
  function mmStartLobbyRefresh() { mmStopLobbyRefresh(); mmLobbyTimer = setInterval(() => { if (mmView === 'lobby') mmRenderLobby(); }, 3000); }
  function mmStopLobbyRefresh() { if (mmLobbyTimer) { clearInterval(mmLobbyTimer); mmLobbyTimer = null; } }

  // ---------- room render (roster + notes) ----------
  /* ---------------------------------------------------------------------
     Video call layer — LiveKit.

     A join token is minted by our own Netlify function, which verifies the
     Firebase session and checks room membership against the Firestore rules
     before signing. The API secret never reaches the browser.

     Cost shape worth remembering when changing anything here: LiveKit bills
     for CONNECTED TIME per participant, not for whether tracks are published.
     So the room itself is a free lobby (presence lives in Firestore) and the
     meter only runs between mmCallStart and mmCallEnd. Every exit path has to
     call mmCallEnd, and the 20-minute cap exists so an abandoned tab cannot
     meter indefinitely.
  --------------------------------------------------------------------- */
  var mmCall = {
    active: false, connecting: false, muted: false, camOff: false,
    room: null,            // LiveKit Room
    speakers: {},          // uid -> true while talking
    els: {},               // participant identity -> attached <video> element
    audioBin: null,        // detached container holding remote <audio> elements
    endsAt: 0,             // timestamp the call auto-disconnects
    ticker: null,          // countdown interval
    expired: false,        // true once the cap has been hit, for the message
    lastSig: '',           // participant/video signature; guards needless rebuilds
    focusUid: null         // identity of the spotlighted participant, if any
  };

  /* Calls are capped.

     LiveKit bills for connected time, so the expensive mistake is not a long
     meeting — it's a call nobody hung up. A tab left open in the background
     meters silently until the laptop is closed. A hard cap turns that from an
     unbounded cost into a bounded one, and anyone genuinely still talking just
     presses the button.

     Note this does NOT touch notes: the editor and everything saved live in
     Firestore independently of the call. Ending a call only drops the media
     connection. */
  const MM_CALL_MAX_MS   = 20 * 60 * 1000;
  const MM_CALL_WARN_MS  = 2 * 60 * 1000;   // show the extend button this early

  /* Control icons. Drawn inline rather than pulled from an icon font so they
     inherit the glass button's colour and need no extra request. */
  const MM_ICON = {
    mic: '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="2" width="6" height="11" rx="3"/><path d="M5 10a7 7 0 0 0 14 0"/><line x1="12" y1="17" x2="12" y2="21"/><line x1="8" y1="21" x2="16" y2="21"/></svg>',
    micOff: '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="2" width="6" height="11" rx="3"/><path d="M5 10a7 7 0 0 0 14 0"/><line x1="12" y1="17" x2="12" y2="21"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="3" y1="3" x2="21" y2="21"/></svg>',
    cam: '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="13" height="12" rx="2"/><path d="M15 11l6-3.5v9L15 13z"/></svg>',
    camOff: '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="13" height="12" rx="2"/><path d="M15 11l6-3.5v9L15 13z"/><line x1="3" y1="3" x2="21" y2="21"/></svg>'
  };

  /* Who is on a call in this room, tracked in Firestore rather than by asking
     LiveKit.

     This matters for cost: LiveKit bills for connected time, so "peek to see
     if a call is running" would meter every person who merely opened the room.
     A flag on the room member document rides the snapshot listener that is
     already open, so presence is effectively free. */
  async function mmSetCallPresence(inCall) {
    if (!(mmTeam() && mmRoomId && mmUid() && fbDb)) return;
    try {
      await mmRoomRef(mmRoomId).collection('members').doc(mmUid())
        .set({ inCall: !!inCall, callSince: inCall ? new Date().toISOString() : '' }, { merge: true });
    } catch (e) { console.warn('Call presence:', e); }
  }

  function mmOthersInCall() {
    const me = mmUid();
    /* A browser that crashes or loses power never gets to clear its flag, so
       a stale one would advertise a call that ended hours ago. Anything older
       than the hard cap cannot still be running, so it is ignored. */
    const stale = Date.now() - (MM_CALL_MAX_MS + 5 * 60 * 1000);
    return (mmRoomMembers || []).filter(m => {
      if (!m.inCall || m.uid === me) return false;
      const t = m.callSince ? new Date(m.callSince).getTime() : 0;
      return !t || t > stale;
    });
  }

  /* The LiveKit SDK is ~200KB, and most sessions in Meeting Mode are note
     taking rather than calls. So it is fetched on the first "Start call"
     rather than on every page load. */
  /* Pinned to an exact version on purpose. "@2" is a RANGE — jsdelivr would
     serve whatever the newest 2.x happens to be, so the code running in your
     users' browsers could change without you deploying anything, and a
     Subresource Integrity hash could never be attached to it. Bump this
     deliberately after testing, the same as any other dependency. */
  const MM_LK_SRC = 'https://cdn.jsdelivr.net/npm/livekit-client@2.21.0/dist/livekit-client.umd.min.js';
  /* Integrity hash for exactly the bytes at that pinned URL. Because this
     script is injected at runtime rather than sitting in the document, the
     attributes have to be set on the element by hand — but the browser
     enforces them identically. Change the version and this must change too,
     or the script will refuse to load. */
  const MM_LK_SRI = 'sha384-/0L8HD1sXzBDqZAU0s9eTwBbWz4KHVXXXT/aIZRsdbLZRm2z8jkLs5VYHJgnHTqj';
  let mmLkLoading = null;
  function mmLoadLiveKit() {
    if (window.LivekitClient) return Promise.resolve(window.LivekitClient);
    if (mmLkLoading) return mmLkLoading;
    mmLkLoading = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = MM_LK_SRC;
      s.integrity = MM_LK_SRI;
      s.crossOrigin = 'anonymous';
      s.referrerPolicy = 'no-referrer';
      s.onload = () => window.LivekitClient ? resolve(window.LivekitClient) : reject(new Error('LiveKit loaded but global missing'));
      // An integrity mismatch also lands here — the browser blocks the script
      // rather than running something that isn't what we pinned.
      s.onerror = () => { mmLkLoading = null; reject(new Error('Could not load the video library. Check your connection, or the file failed its integrity check.')); };
      document.head.appendChild(s);
    });
    return mmLkLoading;
  }

  /* --- AI adapter ---------------------------------------------------------
     Every AI feature in the app goes through this one function, for two
     reasons.

     First, the provider stays swappable. Changing from Anthropic to anyone
     else is a change to netlify/functions/ai.js plus nothing here — the
     browser only ever knows "task name in, text out".

     Second, the browser cannot choose a prompt. It sends a TASK NAME; the
     wording lives server-side in the function's task registry. If the prompt
     were sent from here, anyone could read app.html, find this endpoint, and
     spend our API credits on whatever they liked.

     Note what is NOT sent: no client list, no roster, no team data. Where a
     feature needs to identify a person, the model returns what it heard and
     the matching happens locally against data already in this browser. That
     keeps the roster out of every request, which matters because Terms §8
     forbids PHI and the smallest possible payload is the easiest promise to
     keep. */
  const OTTER_AI_URL = '/.netlify/functions/ai';
  const OTTER_AI_TIMEOUT_MS = 30000;

  async function otterAI(task, input) {
    const user = (typeof currentUser !== 'undefined') ? currentUser : null;
    if (!user) throw new Error('Sign in first.');
    const teamId = (typeof activeTeamId !== 'undefined') ? activeTeamId : null;
    if (!teamId) throw new Error('Open a team first.');

    const idToken = await user.getIdToken();

    /* A hung request is worse than a failed one — the CHW is standing there
       watching a spinner. Give up at 30s and say so. */
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), OTTER_AI_TIMEOUT_MS);

    let res, data;
    try {
      res = await fetch(OTTER_AI_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken, teamId, task, input }),
        signal: ctrl.signal
      });
      data = await res.json().catch(() => ({}));
    } catch (e) {
      if (e && e.name === 'AbortError') throw new Error('That took too long. Try again.');
      throw new Error('Could not reach the AI service. Check your connection.');
    } finally {
      clearTimeout(timer);
    }

    if (!res.ok) {
      // The function deliberately returns a message meant for a person, so it
      // is safe to show. Fall back to the status if something else went wrong.
      const msg = data && data.error ? data.error : ('AI request failed (' + res.status + ').');
      const err = new Error(msg);
      err.status = res.status;
      throw err;
    }

    // Usage comes back on every call. Logging it here means the first time a
    // bill looks wrong, there is something in the console to look at.
    if (data && data.usage) {
      console.log('[ai]', task, 'in', data.usage.input_tokens, 'out', data.usage.output_tokens);
    }
    return data.result;
  }
  window.otterAI = otterAI;

  /* Ask our own Netlify function for a join token. The API secret never
     reaches the browser: the function verifies this Firebase session, checks
     room membership against the Firestore rules, and returns a token good for
     one room for one hour. */
  async function mmFetchCallToken() {
    const user = (typeof currentUser !== 'undefined') ? currentUser : null;
    if (!user) throw new Error('Sign in first.');
    if (!activeTeamId || !mmRoomId) throw new Error('Open a room before starting a call.');
    const idToken = await user.getIdToken();
    const res = await fetch('/.netlify/functions/livekit-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken, teamId: activeTeamId, roomId: mmRoomId, displayName: mmMyName() })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || ('Could not join the call (' + res.status + ').'));
    return data;
  }

  function mmCallPeople() {
    // In a call the roster comes from LiveKit, so a tile appears the moment
    // someone actually connects rather than when they open the room.
    if (mmCall.active && mmCall.room) {
      const lp = mmCall.room.localParticipant;
      const people = [{ uid: lp.identity, name: lp.name || mmMyName(), local: true }];
      mmCall.room.remoteParticipants.forEach(p => {
        people.push({ uid: p.identity, name: p.name || 'Member', local: false });
      });
      return people;
    }
    let people = [];
    if (mmTeam()) {
      people = mmRoomMembers.filter(m => m.active).map(m => ({ uid: m.uid, name: m.name }));
      if (!people.find(p => p.uid === mmUid())) people.unshift({ uid: mmUid(), name: mmMyName() });
    } else {
      people = [{ uid: mmUid(), name: mmMyName() }];
    }
    people.sort((a, b) => (a.uid === mmUid() ? -1 : b.uid === mmUid() ? 1 : 0));
    return people;
  }

  /* Find the participant's published camera track, if any. */
  function mmVideoTrackFor(p) {
    let found = null;
    p.trackPublications.forEach(pub => {
      if (found) return;
      const isCam = pub.source === window.LivekitClient.Track.Source.Camera
                 || pub.source === window.LivekitClient.Track.Source.ScreenShare;
      if (isCam && pub.track && !pub.isMuted) found = pub.track;
    });
    return found;
  }

  function mmParticipantByIdentity(id) {
    if (!mmCall.room) return null;
    if (mmCall.room.localParticipant.identity === id) return mmCall.room.localParticipant;
    let hit = null;
    mmCall.room.remoteParticipants.forEach(p => { if (p.identity === id) hit = p; });
    return hit;
  }

  /* Cheap per-frame update: speaking ring and muted dimming, by toggling
     classes on tiles that already exist. Never touches innerHTML, so video
     elements are never detached and nothing flickers. */
  function mmUpdateTileStates() {
    const wrap = el('mm-tiles');
    if (!wrap || !mmCall.active) return;
    const me = mmUid();
    wrap.querySelectorAll('.mm-tile[data-uid]').forEach(tile => {
      const uid = tile.getAttribute('data-uid');
      const isMe = uid === me;
      const part = mmParticipantByIdentity(uid);
      const micOff = isMe ? mmCall.muted : !!(part && !part.isMicrophoneEnabled);
      tile.classList.toggle('muted', micOff);
      tile.classList.toggle('speaking', !!mmCall.speakers[uid] && !micOff);
      const mic = tile.querySelector('.mm-tile-mic');
      if (micOff && !mic) {
        const s = document.createElement('span');
        s.className = 'mm-tile-mic';
        s.innerHTML = '&#9663;';
        const name = tile.querySelector('.mm-tile-name');
        if (name) name.insertBefore(s, name.firstChild);
      } else if (!micOff && mic) { mic.remove(); }
    });
    /* Deliberately NOT calling mmSetCallUI here. It rewrites the innerHTML of
       the mic and camera buttons, and this function runs several times a
       second while someone talks — which would make the control icons flicker
       for exactly the same reason the tiles did. The countdown and participant
       count are refreshed by the one-second ticker instead. */
  }

  function mmRenderTiles() {
    const wrap = el('mm-tiles');
    if (!wrap) return;
    if (!mmCall.active) { wrap.innerHTML = ''; mmCall.lastSig = ''; return; }
    const me = mmUid();
    const people = mmCallPeople();

    /* Only rebuild when the SET of tiles actually changes — who is present and
       whether they have video. Everything else is a class toggle. Without this
       guard the grid was rebuilt on every speaker change, which destroyed and
       recreated every <video> element several times a second. */
    // If the person being spotlighted has left, drop back to the even grid.
    if (mmCall.focusUid && !people.some(p => p.uid === mmCall.focusUid)) mmCall.focusUid = null;

    const sig = people.map(p => {
      const part = mmParticipantByIdentity(p.uid);
      return p.uid + ':' + (part && mmVideoTrackFor(part) ? '1' : '0');
    }).join('|') + '#' + (mmCall.focusUid || '');
    if (sig === mmCall.lastSig && wrap.querySelector('.mm-tile')) {
      mmUpdateTileStates();
      return;
    }
    mmCall.lastSig = sig;
    wrap.classList.toggle('spotlight', !!mmCall.focusUid);

    /* The shells are rebuilt with innerHTML, which would destroy any <video>
       element inside them and kill the stream. So the media elements live in
       mmCall.els and are re-appended after the rebuild — moving a node is
       cheap and does not interrupt playback. */
    wrap.innerHTML = people.map(p => {
      const isMe = p.uid === me || p.local;
      const cls = ['mm-tile'];
      if (isMe) cls.push('mm-tile-self');
      const part = mmParticipantByIdentity(p.uid);
      const micOff = isMe ? mmCall.muted : !!(part && !part.isMicrophoneEnabled);
      if (micOff) cls.push('muted');
      if (mmCall.speakers[p.uid] && !micOff) cls.push('speaking');
      const first = (p.name || 'Member').split(' ')[0];
      const label = escHtml(first) + (isMe ? ' (you)' : '');
      const mic = micOff ? '<span class="mm-tile-mic">&#9663;</span>' : '';
      const focused = mmCall.focusUid === p.uid;
      if (focused) cls.push('focus');
      const title = focused ? 'Click to shrink' : 'Click to enlarge';
      return `<div class="${cls.join(' ')}" data-uid="${escHtml(p.uid)}" title="${title}">` +
             `<span class="mm-tile-av">${escHtml(mmInitials(p.name))}</span>` +
             `<span class="mm-tile-expand">${focused ? '&#10005;' : '&#9974;'}</span>` +
             `<span class="mm-tile-name">${mic}${label}</span></div>`;
    }).join('');

    // The "waiting" placeholder is clutter once someone is spotlighted.
    if (people.length < 8 && !mmCall.focusUid) {
      wrap.insertAdjacentHTML('beforeend', '<div class="mm-tile mm-tile-empty" style="cursor:default"><span>Waiting for others</span></div>');
    }

    // Click a tile to spotlight it; click the same one again to go back.
    wrap.querySelectorAll('.mm-tile[data-uid]').forEach(t => {
      t.addEventListener('click', () => {
        const uid = t.getAttribute('data-uid');
        mmCall.focusUid = (mmCall.focusUid === uid) ? null : uid;
        mmRenderTiles();
      });
    });

    // Put the live video back, over the initials fallback.
    people.forEach(p => {
      const part = mmParticipantByIdentity(p.uid);
      if (!part) return;
      const track = mmVideoTrackFor(part);
      const tile = wrap.querySelector('.mm-tile[data-uid="' + (window.CSS && CSS.escape ? CSS.escape(p.uid) : p.uid) + '"]');
      if (!tile) return;
      if (!track) { const old = mmCall.els[p.uid]; if (old && old.parentNode) old.parentNode.removeChild(old); return; }
      let elv = mmCall.els[p.uid];
      if (!elv) {
        elv = track.attach();
        elv.setAttribute('playsinline', '');
        elv.muted = true;              // video element audio is handled separately
        mmCall.els[p.uid] = elv;
      }
      const av = tile.querySelector('.mm-tile-av');
      if (av) av.remove();
      tile.insertBefore(elv, tile.firstChild);
    });
  }

  /* Tells the room a call is running, and offers a way in. Only shown to
     people who are NOT already on it. */
  function mmRenderLiveCallBanner() {
    const b = el('mm-livecall');
    if (!b) return;
    const others = mmOthersInCall();
    if (mmCall.active || mmCall.connecting || !others.length) { b.hidden = true; b.innerHTML = ''; return; }
    const names = others.map(m => (m.name || 'Someone').split(' ')[0]);
    const who = names.length === 1 ? names[0] + ' is'
      : names.length === 2 ? names[0] + ' and ' + names[1] + ' are'
      : names.slice(0, 2).join(', ') + ' and ' + (names.length - 2) + ' more are';
    b.innerHTML = '<span class="mm-livedot"></span><span>' + escHtml(who) + ' on a call</span>' +
                  '<button id="mm-joincall-btn">Join call</button>';
    b.hidden = false;
    const j = el('mm-joincall-btn');
    if (j) j.addEventListener('click', () => mmCallStart());
  }

  function mmSetCallUI() {
    const stage = el('mm-stage');
    if (stage) stage.classList.toggle('in-call', mmCall.active);
    const others = mmOthersInCall();
    const btn = el('mm-call-btn');
    if (btn) {
      // "Join" reads very differently from "Start" when a call is already
      // running — the old label made people think they'd begin a second one.
      btn.innerHTML = mmCall.connecting ? '&#9673; Connecting…'
        : mmCall.active ? '&#9673; Leave call'
        : others.length ? ('&#9673; Join call (' + others.length + ')')
        : '&#9673; Start call';
    }
    const mute = el('mm-mute-btn');
    if (mute) {
      mute.classList.toggle('off', mmCall.muted);
      mute.innerHTML = mmCall.muted ? MM_ICON.micOff : MM_ICON.mic;
      const t = mmCall.muted ? 'Unmute microphone' : 'Mute microphone';
      mute.title = t; mute.setAttribute('aria-label', t);
    }
    const cam = el('mm-cam-btn');
    if (cam) {
      cam.classList.toggle('off', mmCall.camOff);
      cam.innerHTML = mmCall.camOff ? MM_ICON.camOff : MM_ICON.cam;
      const t = mmCall.camOff ? 'Turn camera on' : 'Turn camera off';
      cam.title = t; cam.setAttribute('aria-label', t);
    }
    const st = el('mm-call-status');
    const ext = el('mm-extend-btn');
    const left = mmCall.active && mmCall.endsAt ? (mmCall.endsAt - Date.now()) : 0;
    const ending = mmCall.active && left > 0 && left <= MM_CALL_WARN_MS;
    if (ext) ext.hidden = !ending;
    if (st) {
      st.classList.toggle('ending', !!ending);
      if (mmCall.connecting) st.textContent = 'Connecting…';
      else if (mmCall.active) {
        if (ending) {
          st.textContent = 'Ending in ' + mmFmtClock(left);
        } else {
          const others = mmCall.room ? mmCall.room.remoteParticipants.size : 0;
          const who = others === 0 ? 'You are the only one here' : (others === 1 ? '1 other person' : others + ' other people');
          st.textContent = who + (left > 0 ? ' · ' + mmFmtClock(left) + ' left' : '');
        }
      } else st.textContent = '';
    }
  }

  /* Remote audio must be attached to the document to be audible, but it has no
     place in the tile grid — a hidden bin keeps the elements alive without
     affecting layout. */
  function mmAudioBin() {
    if (!mmCall.audioBin) {
      const d = document.createElement('div');
      d.id = 'mm-audio-bin';
      d.style.cssText = 'position:absolute; width:0; height:0; overflow:hidden;';
      document.body.appendChild(d);
      mmCall.audioBin = d;
    }
    return mmCall.audioBin;
  }

  function mmWireRoomEvents(room, LK) {
    const E = LK.RoomEvent;
    const rerender = () => { mmSetCallUI(); mmRenderTiles(); };

    room.on(E.ParticipantConnected, rerender);
    room.on(E.ParticipantDisconnected, p => {
      const elv = mmCall.els[p.identity];
      if (elv && elv.parentNode) elv.parentNode.removeChild(elv);
      delete mmCall.els[p.identity];
      delete mmCall.speakers[p.identity];
      rerender();
    });

    room.on(E.TrackSubscribed, (track) => {
      if (track.kind === LK.Track.Kind.Audio) mmAudioBin().appendChild(track.attach());
      rerender();
    });
    room.on(E.TrackUnsubscribed, (track, pub, participant) => {
      track.detach().forEach(elv => { if (elv.parentNode) elv.parentNode.removeChild(elv); });
      if (track.kind === LK.Track.Kind.Video) delete mmCall.els[participant.identity];
      rerender();
    });

    /* Mute state and speaking state change CONSTANTLY — ActiveSpeakersChanged
       fires several times a second while anyone is talking. These must never
       rebuild the grid: they only toggle classes on tiles that already exist.
       Rebuilding here is what made the tiles visibly blink. */
    room.on(E.TrackMuted, mmUpdateTileStates);
    room.on(E.TrackUnmuted, mmUpdateTileStates);
    room.on(E.LocalTrackPublished, rerender);
    room.on(E.LocalTrackUnpublished, rerender);

    // Who is talking — drives the same accent the roster uses for selection.
    room.on(E.ActiveSpeakersChanged, speakers => {
      mmCall.speakers = {};
      (speakers || []).forEach(s => { mmCall.speakers[s.identity] = true; });
      mmUpdateTileStates();
    });

    room.on(E.Disconnected, () => {
      if (mmCall.active) mmCallEnd(true);
    });
  }

  function mmFmtClock(ms) {
    const s = Math.max(0, Math.round(ms / 1000));
    return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
  }

  function mmStartCallTimer() {
    mmStopCallTimer();
    mmCall.endsAt = Date.now() + MM_CALL_MAX_MS;
    mmCall.ticker = setInterval(() => {
      if (!mmCall.active) { mmStopCallTimer(); return; }
      const left = mmCall.endsAt - Date.now();
      if (left <= 0) { mmCallExpire(); return; }
      mmSetCallUI();
    }, 1000);
  }

  function mmStopCallTimer() {
    if (mmCall.ticker) { clearInterval(mmCall.ticker); mmCall.ticker = null; }
  }

  /* Give the call another 20 minutes. The join token lasts an hour, so this
     can be pressed twice before the token itself expires — after that LiveKit
     drops the connection and a fresh call is needed, which is the same
     outcome, just arrived at differently. */
  function mmExtendCall() {
    if (!mmCall.active) return;
    mmCall.endsAt = Date.now() + MM_CALL_MAX_MS;
    mmSetCallUI();
  }

  function mmCallExpire() {
    mmStopCallTimer();
    mmCallEnd();
    mmCall.expired = true;
    const wrap = el('mm-tiles');
    if (wrap) {
      wrap.innerHTML = '<div class="mm-call-over">The call ended automatically after 20 minutes.' +
        '<br />Your notes are safe — nothing was lost.<br /><br />' +
        '<button class="mm-btn mm-btn-primary" id="mm-restart-call">Start a new call</button></div>';
      const again = el('mm-restart-call');
      if (again) again.addEventListener('click', () => { mmCall.expired = false; mmCallStart(); });
    }
    // Keep the video pane visible so the message is actually seen.
    const stage = el('mm-stage');
    if (stage) stage.classList.add('in-call');
  }

  async function mmCallStart() {
    if (mmCall.active || mmCall.connecting) return;
    mmCall.expired = false;
    mmCall.connecting = true;
    const st = el('mm-call-status'); if (st) st.textContent = 'Connecting…';
    const btn = el('mm-call-btn'); if (btn) btn.innerHTML = '&#9673; Connecting…';
    try {
      const [LK, auth] = await Promise.all([mmLoadLiveKit(), mmFetchCallToken()]);
      /* Quality settings.

         adaptiveStream asks LiveKit for the simulcast layer that matches the
         SIZE OF THE ELEMENT the video is rendered into. That is excellent for
         bandwidth, and it is also why remote faces looked soft while the local
         preview looked sharp: the local tile shows the raw camera, while the
         remote one was being served the smallest layer because the tiles are
         only a couple of hundred pixels wide.

         So: capture at 720p, and publish three simulcast layers. Adaptive
         stream then has a decent layer to pick when a tile is reasonably
         sized, and still drops to a small one when the grid is crowded —
         which is exactly the behaviour worth keeping, since downstream
         bandwidth is what LiveKit bills for. */
      const room = new LK.Room({
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: { resolution: LK.VideoPresets.h720.resolution },
        publishDefaults: {
          simulcast: true,
          videoSimulcastLayers: [LK.VideoPresets.h180, LK.VideoPresets.h360],
          videoEncoding: LK.VideoPresets.h720.encoding
        }
      });
      mmCall.room = room;
      mmWireRoomEvents(room, LK);
      await room.connect(auth.url, auth.token);
      /* Join silent and dark on purpose. Nobody wants to be broadcast the
         instant they click a button, and it also means the browser's camera
         permission prompt only appears when someone actually chooses to turn
         something on. They publish when they press mic or camera. */
      mmCall.active = true;
      mmCall.muted = true;
      mmCall.camOff = true;
      mmStartCallTimer();
      mmSetCallPresence(true);
    } catch (e) {
      const msg = (e && e.message) || String(e);
      // Permission refusal is by far the most common failure and deserves a
      // clearer message than the SDK's.
      alert(/permission|NotAllowed/i.test(msg)
        ? 'Camera and microphone access was blocked. Allow it in the address bar, then try again.'
        : 'Could not start the call: ' + msg);
      try { if (mmCall.room) await mmCall.room.disconnect(); } catch (e2) {}
      mmCall.room = null;
      mmCall.active = false;
    } finally {
      mmCall.connecting = false;
      mmSetCallUI();
      mmRenderTiles();
    }
  }

  function mmCallEnd(alreadyDisconnected) {
    mmStopCallTimer();
    if (mmCall.active) mmSetCallPresence(false);
    const room = mmCall.room;
    mmCall.room = null;
    mmCall.active = false;
    mmCall.connecting = false;
    mmCall.endsAt = 0;
    mmCall.speakers = {};
    mmCall.focusUid = null;
    mmCall.muted = false;
    mmCall.camOff = false;
    Object.keys(mmCall.els).forEach(k => {
      const elv = mmCall.els[k];
      if (elv && elv.parentNode) elv.parentNode.removeChild(elv);
    });
    mmCall.els = {};
    if (mmCall.audioBin) { mmCall.audioBin.innerHTML = ''; }
    if (room && !alreadyDisconnected) { try { room.disconnect(); } catch (e) {} }
    mmRenderLiveCallBanner();
    mmSetCallUI();
    mmRenderTiles();
  }

  async function mmToggleMute() {
    if (!mmCall.active || !mmCall.room) return;
    mmCall.muted = !mmCall.muted;
    try { await mmCall.room.localParticipant.setMicrophoneEnabled(!mmCall.muted); }
    catch (e) { mmCall.muted = !mmCall.muted; console.warn('Mic toggle failed:', e); }
    mmSetCallUI();
    mmRenderTiles();
  }

  async function mmToggleCam() {
    if (!mmCall.active || !mmCall.room) return;
    mmCall.camOff = !mmCall.camOff;
    try { await mmCall.room.localParticipant.setCameraEnabled(!mmCall.camOff); }
    catch (e) { mmCall.camOff = !mmCall.camOff; console.warn('Camera toggle failed:', e); }
    mmSetCallUI();
    mmRenderTiles();
  }

  function mmRenderRoom() {
    const crumb = el('mm-crumb-live'); if (crumb) crumb.innerHTML = '<span class="mm-live-dot"></span>' + escHtml(mmRoomName || 'Room');
    const title = el('mm-title'); if (title) title.textContent = mmRoomType === 'public' ? 'Team room' : (mmRoomOwnerUid === mmUid() ? 'Your notes' : (mmRoomName || 'Room'));

    const banner = el('mm-req-banner');
    if (banner) {
      if (mmRoomType === 'private' && mmRoomOwnerUid === mmUid() && mmRoomRequests.length) {
        banner.innerHTML = mmRoomRequests.map(r => `<div class="mm-req"><span><b>${escHtml(r.name || 'Someone')}</b> wants to join</span><span class="mm-req-btns"><button class="mm-req-yes" data-uid="${escHtml(r.uid)}" data-name="${escHtml(r.name || '')}">Let in</button><button class="mm-req-no" data-uid="${escHtml(r.uid)}">Dismiss</button></span></div>`).join('');
        banner.style.display = 'block';
        banner.querySelectorAll('.mm-req-yes').forEach(b => b.addEventListener('click', () => mmApprove(b.dataset.uid, b.dataset.name)));
        banner.querySelectorAll('.mm-req-no').forEach(b => b.addEventListener('click', () => mmDeny(b.dataset.uid)));
      } else { banner.style.display = 'none'; banner.innerHTML = ''; }
    }

    const bubbles = el('mm-bubbles');
    const roster = el('mm-roster');

    if (mmRoomPending) {
      if (roster) roster.innerHTML = '';
      if (bubbles) bubbles.innerHTML = `<div class="mm-pending">Waiting for ${escHtml(mmRoomName.replace(/'s room$/, ''))} to let you in&hellip;</div>`;
      mmToggleAddAll(false);
      const cb = el('mm-call-btn'); if (cb) cb.style.display = 'none';
      return;
    }
    // Admitted: the call entry point follows room membership, so someone
    // still waiting for approval can't join the call either.
    const cbShow = el('mm-call-btn');
    if (cbShow && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) cbShow.style.display = 'inline-flex';
    // Keep tiles in step with the roster as people come and go.
    mmRenderLiveCallBanner();
    mmSetCallUI();
    mmRenderTiles();

    if (roster) {
      let people = [];
      if (mmTeam()) {
        people = mmRoomMembers.filter(m => m.active).map(m => ({ uid: m.uid, name: m.name }));
        if (!people.find(p => p.uid === mmUid())) people.unshift({ uid: mmUid(), name: mmMyName() });
      } else {
        people = [{ uid: mmUid(), name: mmMyName() }];
      }
      /* Roster is a presence display only — everyone's notes show together
         below. The green dot separates "in the room" from "on the call": the
         room is a lobby you can sit in for free, and only the dotted people
         are actually connected to LiveKit. */
      const onCallUids = {};
      (mmRoomMembers || []).forEach(m => { if (m.inCall) onCallUids[m.uid] = true; });
      if (mmCall.active) onCallUids[mmUid()] = true;
      roster.innerHTML = people.map(p => {
        const meTag = p.uid === mmUid() ? ' (you)' : '';
        const onCall = !!onCallUids[p.uid];
        const dot = onCall ? '<span class="mm-ros-live" title="On the call"></span>' : '';
        return `<span class="mm-ros${onCall ? ' oncall' : ''}" style="cursor:default">${dot}<span class="mm-ros-av">${escHtml(mmInitials(p.name))}</span><span class="mm-ros-name">${escHtml((p.name || 'Member').split(' ')[0])}${meTag}</span></span>`;
      }).join('');
    }

    const myUid = mmUid();
    // Everyone's notes I'm allowed to see: my own (all) + others' PUBLIC ones — shown together.
    const notes = mmAllNotes()
      .filter(n => n.authorUid === myUid || n.public === true)
      .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0))
      .slice(0, 40);
    const cards = notes.map(n => {
      const mine = n.authorUid === myUid;
      const clients = mmParseClients(n.clientsJson);
      const who = clients[0] ? ('&#9670; ' + escHtml(clients[0])) : '&#9670; No client';
      const author = mine ? 'You' : escHtml(n.authorName || 'Member');
      let raw = (n.title ? n.title + ' — ' : '') + (n.body || ''); raw = raw.length > 90 ? raw.slice(0, 90) + '…' : raw;
      const preview = escHtml(raw) || 'Untitled note';
      const t = mmTimeLabel(n.updatedAt || n.createdAt);
      const pub = n.public === true;
      const shareBtn = mine
        ? `<button class="mm-bubble-share ${pub ? 'on' : ''}" data-share="${escHtml(n.id)}" title="${pub ? 'Public — click to make private' : 'Private — click to share with the room'}">${pub ? 'Public' : 'Private'}</button>`
        : '<span class="mm-bubble-share on" style="cursor:default">Public</span>';
      const addBtn = mine ? `<button class="mm-bubble-add" data-add="${escHtml(n.id)}" title="Copy into your Notes section">${n.addedToNotes ? '✓ In Notes' : '+ Notes'}</button>` : '';
      // Room notes live in rooms/{id}/notes — a different collection from the
      // Notes board. Deleting the copy on the board never removed the original,
      // which left notes stranded in the room with no way to clear them.
      const delBtn = mine ? `<button class="mm-bubble-del" data-del="${escHtml(n.id)}" title="Delete this note from the room">&times;</button>` : '';
      return `<div class="mm-bubble" data-id="${escHtml(n.id)}" data-mine="${mine ? '1' : '0'}">
        <div class="mm-bubble-top"><span class="mm-bubble-who">${who}</span>${shareBtn}${delBtn}</div>
        <span class="mm-bubble-body">${preview}</span>
        <div class="mm-bubble-foot"><span class="mm-bubble-time">${author}${t ? ' &middot; ' + t : ''}</span>${addBtn}</div>
      </div>`;
    }).join('');
    const create = `<div class="mm-bubble mm-bubble-create" id="mm-bubble-create"><span class="mm-plus">+</span><span class="mm-create-label">Create note</span></div>`;
    if (bubbles) {
      bubbles.innerHTML = cards + create;
      bubbles.querySelectorAll('.mm-bubble-share[data-share]').forEach(b => b.addEventListener('click', e => { e.stopPropagation(); mmToggleNotePublic(b.dataset.share); }));
      bubbles.querySelectorAll('.mm-bubble-add').forEach(b => b.addEventListener('click', e => { e.stopPropagation(); mmAddNoteToNotes(b.dataset.add); }));
      bubbles.querySelectorAll('.mm-bubble-del[data-del]').forEach(b => b.addEventListener('click', e => { e.stopPropagation(); mmDeleteRoomNote(b.dataset.del); }));
      bubbles.querySelectorAll('.mm-bubble[data-mine="1"]').forEach(b => b.addEventListener('click', () => mmOpenEditor(b.dataset.id)));
      const c = el('mm-bubble-create'); if (c) c.addEventListener('click', () => mmOpenEditor(null));
    }
    mmToggleAddAll(notes.some(n => n.authorUid === myUid && !n.addedToNotes));
  }
  function mmToggleAddAll(show) { const b = el('mm-addall-btn'); if (b) b.style.display = show ? 'inline-flex' : 'none'; }

  // ---------- editor ----------
  /* ONE document per person per room, rather than a pile of notes.

     Opening "my notes" with no id used to start a blank one every time, so a
     meeting ended up scattered across half a dozen fragments that then had to
     be archived and read separately. Now it reopens whatever you already have
     in this room and you keep typing — which is what people were doing on
     paper anyway.

     It is deliberately one document PER PERSON and not one shared document:
     Firestore is last-write-wins, so two CHWs typing into the same record
     would quietly overwrite each other. Real co-editing needs a CRDT, and that
     is a much bigger thing than this needs to be. */
  function mmMyRoomNote() {
    const me = mmUid();
    return mmAllNotes().find(n => n.authorUid === me) || null;
  }

  function mmOpenEditor(id) {
    if (!id) { const mine = mmMyRoomNote(); if (mine) id = mine.id; }
    mmEditingId = id || null; mmClient = null; mmEditPublic = false;
    let title = '', body = '';
    if (id) { const n = mmAllNotes().find(x => x.id === id); if (n) { title = n.title || ''; body = n.body || ''; mmEditPublic = n.public === true; const cs = mmParseClients(n.clientsJson); mmClient = cs[0] || null; } }
    el('mm-note-title').value = title; el('mm-note-body').value = body;
    mmTextUndo = null; mmShowUndo(false);
    mmRenderChips(); mmUpdateVisBtn(); mmSetView('editor');
    setTimeout(() => el('mm-note-body').focus(), 130);
  }
  function mmUpdateVisBtn() {
    const b = el('mm-editor-visbtn'); if (!b) return;
    b.classList.toggle('pub', mmEditPublic);
    b.textContent = mmEditPublic ? 'Public — room can see' : 'Private to me';
  }
  async function mmToggleEditVis() {
    mmEditPublic = !mmEditPublic;
    mmUpdateVisBtn();
    // If we're editing a saved note, apply immediately.
    if (mmEditingId) { try { await mmNoteUpdate(mmEditingId, { public: mmEditPublic }); } catch (e) {} }
  }
  async function mmSaveEditor(opts) {
    opts = opts || {};

    /* Dictation lands in the side transcript, not the note. If any of it was
       never moved across, fold it in rather than silently dropping it — losing
       a call transcript would be far worse than an extra paragraph. */
    const trEl = el('mm-transcript-text');
    const pending = trEl ? (trEl.value || '').trim() : '';
    if (pending) {
      const take = opts.silent
        ? true                                    // auto-save path: always keep it
        : confirm('Your transcript hasn\'t been added to the note yet.\n\nAdd it now? (Cancel keeps writing.)');
      if (take) mmTranscriptToNote('insert');
      else return false;
    }

    const title = (el('mm-note-title').value || '').trim();
    const body = (el('mm-note-body').value || '').trim();
    if (!title && !body) { if (!opts.silent) mmToast('Nothing to save yet', true); return false; }
    const wasNew = !mmEditingId;
    const clientsJson = JSON.stringify(mmClient ? [mmClient] : []);
    try {
      if (mmEditingId) { await mmNoteUpdate(mmEditingId, { title, body, clientsJson, public: mmEditPublic }); }
      else { mmEditingId = await mmNoteAdd({ title, body, clientsJson, isPublic: mmEditPublic }); }
    } catch (e) {
      const msg = (e && e.message) ? e.message : String(e);
      if (!opts.silent) {
        if (/permission|insufficient/i.test(msg)) {
          alert('Could not save: your Firestore rules don\'t allow the Meeting Mode "rooms" yet.\n\nIn the Firebase console → Firestore → Rules, add a rooms rule inside /teams/{teamId} (see firestore-rules.txt), then Publish.');
        } else { alert('Could not save note: ' + msg); }
      }
      return false;
    }
    if (wasNew && mmEditingId && !mmSessionNoteIds.includes(mmEditingId)) mmSessionNoteIds.push(mmEditingId);
    mmLastClient = mmClient;
    if (!opts.silent) { mmToast('Saved ✓'); mmSetView('room'); }
    return true;
  }
  async function mmEditorToCalendar() { await mmSaveEditor({ silent: true }); mmLastClient = mmClient; mmSetView('calendar'); }

  /* ---------------- Dictation (speech → text) ----------------
     Runs entirely in THIS browser via the Web Speech API, so it is inherently
     per-person: if two people in a room both switch it on, each transcribes
     their own microphone into their own note. Nothing is shared until the note
     is marked Public.

     Note: on Chrome/Edge the audio is processed by the browser's speech
     service (Google), not by OtterLabs. Don't dictate anything you wouldn't
     put in the note by hand. */
  const MM_SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  let mmRec = null;          // active recognition object
  let mmRecOn = false;       // are we listening?

  /* Read by idleBusy() in app.html. A FUNCTION, not a copied value: mmCall and
     mmRecOn change throughout a call, and a snapshot taken at load would say
     "not busy" forever.

     This exists because these two used to be plain globals in the same script
     as idleBusy(). Moving this file into the bundle put them in module scope,
     idleBusy's `typeof mmCall !== 'undefined'` guard quietly went false, and
     the idle timer stopped treating a live call as activity — which would have
     signed a CHW out in the middle of one. */
  window.mmBusy = () => !!((typeof mmCall !== 'undefined' && mmCall && mmCall.active) || mmRecOn);
  let mmRecWantOn = false;   // user intent (used to auto-restart)
  let mmRecBase = '';        // note body as it was when dictation started
  let mmRecLastAt = 0;       // timestamp of the last final phrase (for paragraph breaks)

  /* --- Transcript shaping ---------------------------------------------------
     Speech APIs return unpunctuated, uncapitalised runs of words. These helpers
     turn that into something readable, and (optionally) into a short summary.
     All of it is plain pattern-matching that runs in the browser — no AI
     service, nothing leaves the device beyond what dictation already sends. */
  /* ===== Transcript intelligence =========================================
     Speech APIs return unpunctuated, uncapitalised, filler-laden text. These
     helpers turn a raw dictation into something a CHW can actually file:
     punctuated prose, then a structured summary that separates what was said
     from what somebody has to DO about it.

     All of it is deterministic pattern-matching running in this browser — no
     AI service, no extra data leaving the device, no per-call cost. ==== */

  // Fillers, stutters and hedges that add nothing to a call note
  const MM_FILLERS = /\b(um+|uh+|erm?|ah+|hmm+|like i mean|you know what i mean|you know|i mean|sort of|kinda|kind of|basically|literally|actually just|i guess|or whatever)\b[,]?\s*/gi;

  // Who owes the follow-up
  const MM_ACT_MINE = /\b(i(?:'| a|'l)?m? ?(?:will|'ll|am going to|going to|gonna|need to|needs to|have to|has to|should|must|plan to)|i said i would|i told (?:her|him|them) i would|let me|i'?ll)\b/i;
  const MM_ACT_THEIRS = /\b((?:she|he|they|the client|the member|her daughter|his daughter|her son|his son)\s+(?:will|needs? to|has to|have to|should|must|is going to|are going to|is supposed to))\b/i;
  const MM_ACT_ANY = /\b(follow[- ]?up|call back|schedule|reschedule|book|set up|send|bring|drop off|pick up|remind|sign up|renew|apply|submit|fax|e-?mail|refer|make an appointment|deadline|due)\b/i;

  const MM_WHEN = /\b(today|tomorrow|tonight|this (?:morning|afternoon|evening|week|month)|next (?:week|month|monday|tuesday|wednesday|thursday|friday|saturday|sunday)|monday|tuesday|wednesday|thursday|friday|saturday|sunday|jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t|tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|\d{1,2}\s?(?:am|pm)|\d{1,2}:\d{2})\b/i;
  const MM_QUESTION = /^(do|does|did|can|could|will|would|is|are|was|were|have|has|had|should|what|when|where|why|how|who|which)\b/i;

  // Words too common to signal what a call was about
  const MM_STOP = new Set(('a an and are as at be been but by for from had has have he her hers him his i if in into is it its me my no not of on or our she so than that the their them then there these they this to was we were what when which who will with would you your about just got get said say says need needs also her him them do does did been being over under again very can could should there here now then').split(' '));

  function mmCapitalize(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

  /* Generic words that appear inside place/org names — capitalising these on
     their own produces things like "the Clinic", so they're skipped. */
  const MM_NAME_STOP = new Set(['clinic','center','centre','health','office','home','house','park','street',
    'road','avenue','drive','lane','court','way','suite','apartment','building','hospital','pharmacy','plan',
    'services','service','department','county','city','north','south','east','west','the','and','for']);

  function mmKnownNameWords() {
    const words = new Set();
    const add = name => String(name || '').split(/\s+/).forEach(w => {
      const clean = w.replace(/[^A-Za-z'-]/g, '');
      if (clean.length > 2 && !MM_NAME_STOP.has(clean.toLowerCase())) words.add(clean);
    });
    try {
      (typeof teamClientsCache !== 'undefined' ? teamClientsCache : []).forEach(c => add(c.name));
      if (typeof loadClients === 'function') loadClients().forEach(c => add(c.name));
      if (typeof loadPlaces === 'function') loadPlaces().forEach(p => add(p.name));
    } catch (e) {}
    return words;
  }
  function mmCapitalizeKnownNames(text) {
    const words = mmKnownNameWords();
    if (!words.size) return text;
    const proper = {};
    words.forEach(w => { proper[w.toLowerCase()] = w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(); });
    return text.replace(/\b[a-z][a-z'-]{2,}\b/g, m => proper[m.toLowerCase()] || m);
  }

  /* Cues that reliably begin a new thought in spoken English. */
  const MM_BREAKERS = [
    'and then', 'so then', 'after that', 'but then', 'and also', 'also', 'but', 'however',
    'meanwhile', 'anyway', 'then', 'so', 'because of that', 'plus',
    "i need to", "i'll", 'i will', 'i said', 'i told', 'i asked', 'i let', 'i have to', 'i am going to', "i'm going to",
    'she said', 'he said', 'they said', 'she told', 'he told', 'they told',
    'she asked', 'he asked', 'they asked', 'she mentioned', 'he mentioned', 'she wanted',
    'she needs', 'he needs', 'they need', 'she wants', 'he wants', 'they want',
    'she also', 'he also', 'they also', 'she has', 'he has', 'they have',
    'she is', 'he is', 'they are', 'her daughter', 'his daughter', 'her son', 'his son'
  ];
  const MM_BREAK_RE = new RegExp('\\s+(' + MM_BREAKERS.join('|') + ')\\s+', 'gi');
  const MM_MIN_WORDS = 5;

  /* Turn a raw run of speech into punctuated, capitalised sentences. */
  function mmShapeSpeech(raw) {
    let t = (raw || '').replace(/\s+/g, ' ').trim();
    if (!t) return '';
    t = t.replace(MM_FILLERS, ' ');
    t = t.replace(/\b(\w+)(\s+\1\b)+/gi, '$1');          // stutters: "the the" -> "the"
    t = t.replace(/\bperiod\b/gi, '.').replace(/\bcomma\b/gi, ',')
         .replace(/\bquestion mark\b/gi, '?').replace(/\bnew (?:line|paragraph)\b/gi, '\n');

    const parts = [];
    let buf = '';
    t.split(MM_BREAK_RE).forEach((piece, i) => {
      if (!piece) return;
      const isCue = (i % 2 === 1);
      if (isCue && buf.trim().split(/\s+/).length >= MM_MIN_WORDS) { parts.push(buf.trim()); buf = piece + ' '; }
      else buf += piece + ' ';
    });
    if (buf.trim()) parts.push(buf.trim());

    /* Speech often strings several clauses together with no cue word at all.
       Any segment still running long gets split again at a subject pronoun,
       provided both halves are substantial enough to stand alone. */
    const MAX_WORDS = 22;
    const SUBJ_RE = /\s+(?=(?:she|he|they|i|we|her daughter|his daughter)\s+(?:said|asked|will|can|is|are|was|were|has|have|needs?|wants?|told|mentioned|got|did|does)\b)/i;
    const split2 = [];
    parts.forEach(seg => {
      let cur = seg;
      let guard = 0;
      while (cur.split(/\s+/).length > MAX_WORDS && guard++ < 6) {
        const m = cur.search(SUBJ_RE);
        if (m <= 0) break;
        const head = cur.slice(0, m).trim();
        const tail = cur.slice(m).trim();
        if (head.split(/\s+/).length < MM_MIN_WORDS || tail.split(/\s+/).length < MM_MIN_WORDS) break;
        split2.push(head);
        cur = tail;
      }
      if (cur.trim()) split2.push(cur.trim());
    });
    parts.length = 0; Array.prototype.push.apply(parts, split2);

    const shaped = parts.map(p => {
      let s = p.replace(/\s+([.,?!])/g, '$1').replace(/\s+/g, ' ').trim();
      s = s.replace(/[\s,]+(and|but|so|or|then|because|plus)$/i, '').trim();
      if (!s) return '';
      if (!/[.?!]$/.test(s)) s += MM_QUESTION.test(s) ? '?' : '.';
      return s.charAt(0).toUpperCase() + s.slice(1);
    }).filter(Boolean);

    let out = shaped.join(' ');
    out = out.replace(/\bi\b/g, 'I').replace(/\bi'(m|ll|ve|d)\b/gi, (m, g) => "I'" + g.toLowerCase());
    out = mmCapitalizeKnownNames(out);
    out = out.replace(/\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday|january|february|march|april|june|july|august|september|october|november|december)\b/gi,
      d => d.charAt(0).toUpperCase() + d.slice(1).toLowerCase());
    out = out.replace(/([.?!]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase());
    return out;
  }

  function mmFormatTranscript(text) {
    return (text || '').split(/\n{2,}/).map(b => mmShapeSpeech(b)).filter(Boolean).join('\n\n');
  }

  function mmSentences(text) {
    return (text || '').replace(/\n+/g, ' ').split(/(?<=[.?!])\s+/)
      .map(s => s.trim()).filter(s => s.split(/\s+/).length >= 3);
  }

  /* Concrete facts worth pulling to the top of a call note. */
  function mmExtractDetails(text) {
    const out = [];
    const seen = new Set();
    const push = (label, val) => {
      const k = label + '|' + val.toLowerCase();
      if (!seen.has(k)) { seen.add(k); out.push(label + ': ' + val); }
    };
    const phones = text.match(/\b(?:\+?1[-. ])?\(?\d{3}\)?[-. ]?\d{3}[-. ]?\d{4}\b/g) || [];
    phones.forEach(p => push('Phone', p.trim()));
    const money = text.match(/\$\s?\d[\d,]*(?:\.\d{2})?\b/g) || [];
    money.forEach(m => push('Amount', m.replace(/\s/g, '')));
    const addr = text.match(/\b\d{2,5}\s+[A-Z][A-Za-z]*(?:\s+[A-Z][A-Za-z]*){0,3}\s+(?:Street|St|Avenue|Ave|Road|Rd|Drive|Dr|Lane|Ln|Court|Ct|Way|Boulevard|Blvd|Circle|Cir|Place|Pl)\b/g) || [];
    addr.forEach(a => push('Address', a.trim()));
    const ids = text.match(/\b(?:cin|case|member|id)\s*(?:number|#)?\s*[:#]?\s*([A-Z0-9-]{4,})\b/gi) || [];
    ids.forEach(v => push('Reference', v.replace(/\s+/g, ' ').trim()));
    return out;
  }

  /* Frequency-based importance: sentences carrying the call's recurring
     content words score higher than filler-y ones. */
  function mmScoreSentences(sentences) {
    const freq = {};
    sentences.forEach(s => s.toLowerCase().match(/\b[a-z']{3,}\b/g)?.forEach(w => {
      if (!MM_STOP.has(w)) freq[w] = (freq[w] || 0) + 1;
    }));
    return sentences.map(s => {
      const words = s.toLowerCase().match(/\b[a-z']{3,}\b/g) || [];
      const content = words.filter(w => !MM_STOP.has(w));
      if (!content.length) return { s, score: 0 };
      let score = content.reduce((a, w) => a + (freq[w] || 0), 0) / Math.sqrt(content.length);
      if (MM_WHEN.test(s)) score *= 1.15;
      if (/\d/.test(s)) score *= 1.1;
      if (words.length < 6) score *= 0.7;              // very short = usually filler
      return { s, score };
    });
  }

  function mmDedupe(list) {
    const seen = new Set(); const out = [];
    list.forEach(s => {
      const k = s.toLowerCase().replace(/[^a-z0-9 ]/g, '').split(/\s+/).slice(0, 8).join(' ');
      if (!seen.has(k)) { seen.add(k); out.push(s); }
    });
    return out;
  }
  function mmTrimBullet(s) {
    return s.replace(/^(so|but|and|also|then|well|okay|ok)\s+/i, '')
            .replace(/^./, c => c.toUpperCase());
  }

  /* Structured summary: key points, action items split by owner, dates, facts. */
  function mmSummarize(text) {
    const formatted = mmFormatTranscript(text);
    const sentences = mmSentences(formatted);
    if (!sentences.length) return '';

    const mine = [], theirs = [], dates = [], rest = [];
    sentences.forEach(s => {
      const isAction = MM_ACT_ANY.test(s) || MM_ACT_MINE.test(s) || MM_ACT_THEIRS.test(s);
      if (isAction && MM_ACT_MINE.test(s)) mine.push(s);
      else if (isAction && MM_ACT_THEIRS.test(s)) theirs.push(s);
      else if (isAction) mine.push(s);                 // an action with no clear owner is on us
      else { rest.push(s); if (MM_WHEN.test(s)) dates.push(s); }
    });

    const ranked = mmScoreSentences(rest).sort((a, b) => b.score - a.score)
      .slice(0, Math.min(6, Math.max(3, Math.round(rest.length * 0.4))))
      .map(x => x.s);
    const order = s => sentences.indexOf(s);
    ranked.sort((a, b) => order(a) - order(b));

    const details = mmExtractDetails(formatted);
    const out = [];
    const section = (title, items) => {
      if (!items.length) return;
      if (out.length) out.push('');
      out.push(title);
      mmDedupe(items).forEach(s => out.push('• ' + mmTrimBullet(s)));
    };

    section('KEY POINTS', ranked);
    section('MY FOLLOW-UPS', mine);
    section('CLIENT / OTHER PARTY', theirs);
    section('DATES MENTIONED', dates.filter(d => ranked.indexOf(d) === -1).slice(0, 5));
    if (details.length) {
      if (out.length) out.push('');
      out.push('DETAILS CAPTURED');
      details.forEach(d => out.push('• ' + d));
    }
    return out.join('\n');
  }

  /* Suggest a note title from the call's opening line + attached client. */
  function mmSuggestTitle(text) {
    const s = mmSentences(mmFormatTranscript(text))[0] || '';
    if (!s) return '';
    let t = mmTrimBullet(s).replace(/[.?!]$/, '');
    if (t.length > 60) t = t.slice(0, 57).replace(/\s+\S*$/, '') + '…';
    return t;
  }

  /* ----- Microphone control ----- */
  function mmMicSupported() { return !!MM_SR; }

  /* The transcript lives beside the note so dictation never takes over the
     field the CHW is typing in. */
  function mmShowTranscript(show) {
    const pane = el('mm-transcript-pane');
    if (pane) pane.hidden = !show;
    // Reserve the drawer's space in the editor so the note is not written
    // underneath it.
    const view = el('mm-view-editor');
    if (view) view.classList.toggle('with-transcript', !!show);
    mmRefreshTranscriptChip();
  }
  /* The header chip only appears when there is a hidden transcript to bring back. */
  function mmRefreshTranscriptChip() {
    const chip = el('mm-transcript-show');
    const pane = el('mm-transcript-pane');
    const tr = el('mm-transcript-text');
    if (!chip) return;
    const hasText = !!(tr && (tr.value || '').trim());
    const hidden = !pane || pane.hidden;
    chip.hidden = !(hidden && (hasText || mmRecWantOn));
  }

  function mmSetMicUI(on) {
    const btn = el('mm-mic-btn'), lab = el('mm-mic-label'), interim = el('mm-interim');
    if (btn) btn.classList.toggle('on', on);
    if (lab) lab.textContent = on ? 'Listening… tap to stop' : 'Transcribe';
    if (!on && interim) { interim.hidden = true; interim.textContent = ''; }
    const pane = el('mm-transcript-pane');
    if (pane) pane.classList.toggle('live', !!on);
    const dLab = el('mm-drawer-label');
    if (dLab) dLab.textContent = on ? 'Live transcript' : 'Transcript';
    mmRefreshTranscriptChip();
  }

  function mmStopMic() {
    mmRecWantOn = false;
    if (mmRec) { try { mmRec.stop(); } catch (e) {} }
    mmRec = null; mmRecOn = false;
    mmSetMicUI(false);
  }

  async function mmStartMic() {
    if (!mmMicSupported()) {
      alert('This browser can\'t do speech-to-text.\n\nChrome or Edge on a computer works best. Safari is partial and Firefox doesn\'t support it at all.');
      return;
    }
    if (!window.isSecureContext) {
      alert('Dictation needs a secure (https) connection.');
      return;
    }
    const body = el('mm-transcript-text');
    if (!body) { mmToast('Open a note first', true); return; }
    mmShowTranscript(true);

    /* Ask for the microphone explicitly.

       SpeechRecognition does not reliably raise its own permission prompt — on
       some setups it just starts and silently returns nothing, which looks like
       the feature is broken. Requesting the stream first forces the prompt and
       gives a real error we can explain. The stream is released immediately;
       recognition opens its own. */
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(t => { try { t.stop(); } catch (e) {} });
      }
    } catch (permErr) {
      const n = permErr && permErr.name;
      if (n === 'NotAllowedError' || n === 'SecurityError') {
        alert('Microphone access was denied.\n\nClick the padlock (or camera icon) in the address bar, allow the microphone for this site, then try again.');
      } else if (n === 'NotFoundError' || n === 'DevicesNotFoundError') {
        alert('No microphone was found on this device.');
      } else {
        alert('Could not open the microphone: ' + (n || permErr));
      }
      return;
    }

    mmRecBase = body.value;                     // keep whatever is already typed
    mmRecWantOn = true;

    const rec = new MM_SR();
    const langSel = el('mm-mic-lang');
    rec.lang = (langSel && langSel.value) || 'en-US';
    rec.continuous = true;
    rec.interimResults = true;

    let finalText = '';
    mmRecLastAt = Date.now();
    rec.onresult = e => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const chunk = e.results[i][0].transcript;
        if (e.results[i].isFinal) {
          // A long pause reads as a new thought — start a new paragraph.
          const now = Date.now();
          const gap = now - mmRecLastAt;
          mmRecLastAt = now;
          const shaped = mmShapeSpeech(chunk);
          if (!shaped) continue;
          if (!finalText) finalText = shaped;
          else finalText += (gap > 2500 ? '\n\n' : ' ') + shaped;
        } else interim += chunk;
      }
      const joiner = mmRecBase ? (/\n$/.test(mmRecBase) ? '' : '\n\n') : '';
      body.value = mmRecBase + joiner + finalText;
      const box = el('mm-interim');
      if (box) { box.hidden = !interim; box.textContent = interim ? '… ' + interim : ''; }
      body.scrollTop = body.scrollHeight;
      mmRefreshTranscriptChip();
    };
    rec.onerror = ev => {
      const code = ev && ev.error;
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        mmStopMic();
        alert('Microphone access was blocked.\n\nAllow the microphone for this site in your browser settings, then try again.');
      } else if (code === 'network') {
        /* Speech recognition streams audio to the BROWSER VENDOR's speech
           service — it isn't done on the device. Chrome and Edge ship with
           access to Google's; most other Chromium browsers (Brave, Vivaldi,
           Arc, ChatGPT Atlas…) expose the API but have no working backend, so
           every attempt returns "network". Retrying can't fix it, so stop. */
        mmStopMic();
        alert('Dictation isn\'t available in this browser.\n\n' +
              'The speech service it relies on is only wired up in Chrome and Edge. ' +
              'This browser has the feature but no working speech backend, so it returns a network error every time.\n\n' +
              'Open the app in Chrome or Edge to use dictation. Everything else works fine here.');
      } else if (code === 'audio-capture') {
        mmStopMic();
        alert('No microphone could be captured.\n\nCheck that a microphone is connected and not in use by another app.');
      } else if (code === 'no-speech' || code === 'aborted') {
        // harmless — the auto-restart below picks it back up
      } else {
        mmToast('Dictation error: ' + (code || 'unknown'), true);
      }
    };
    let restarts = 0, firstRestartAt = 0;
    rec.onend = () => {
      // Continuous recognition still stops on its own; restart while the user
      // still wants it on, carrying the text so far forward as the new base.
      mmRecOn = false;
      if (!mmRecWantOn) { mmSetMicUI(false); return; }

      // Guard against a restart storm: if the service keeps dropping us
      // immediately, stop rather than hammering it forever.
      const now = Date.now();
      if (!firstRestartAt || now - firstRestartAt > 10000) { firstRestartAt = now; restarts = 0; }
      if (++restarts > 6) {
        mmStopMic();
        mmToast('Dictation kept dropping out — stopped', true);
        return;
      }

      mmRecBase = body.value;
      try { rec.start(); mmRecOn = true; } catch (e) { mmSetMicUI(false); }
    };

    let heardSomething = false;
    const origOnResult = rec.onresult;
    rec.onresult = e => { heardSomething = true; origOnResult(e); };

    try {
      rec.start();
      mmRec = rec; mmRecOn = true;
      mmSetMicUI(true);
      mmToast('Listening — speak normally');
      // If nothing at all is picked up in the first 8s, say so rather than
      // leaving the user talking to a button that looks active but isn't.
      setTimeout(() => {
        if (mmRecWantOn && !heardSomething) {
          mmToast('No speech detected yet — check your microphone', true);
        }
      }, 8000);
    } catch (e) {
      mmStopMic();
      const msg = (e && e.message) || String(e);
      // start() throws if a previous session is still winding down
      if (/already started/i.test(msg)) { mmToast('Already listening', true); }
      else { alert('Could not start dictation: ' + msg); }
    }
  }

  function mmToggleMic() {
    if (mmRecOn || mmRecWantOn) {
      // Turning it off surfaces everything captured so far.
      mmStopMic();
      const tr = el('mm-transcript-text');
      if (tr && (tr.value || '').trim()) mmShowTranscript(true);
    } else {
      mmStartMic().catch(e => { console.warn('mic:', e); mmToast('Dictation failed to start', true); });
    }
  }

  /* ---- What to keep from a recording ------------------------------------
     Asked BEFORE the microphone starts, not after.

     The two outputs are genuinely different things and people want different
     ones. A full transcript is a record of what was said — useful, and the
     riskiest text in the app, because it is unedited speech. A summary is a
     reading of it, shorter and safer to pass around. Deciding up front means
     nobody discovers afterwards that the app kept a verbatim record of a
     conversation they thought was being summarised. */
  let mmTrWant = { full: true, summary: false };

  function mmAskTranscribe() {
    const dlg = el('mm-trq-dlg');
    if (!dlg) {
      // No dialog in the DOM: fall back to just recording rather than blocking.
      mmOpenEditor(null);
      setTimeout(() => mmStartMic().catch(e => console.warn('mic:', e)), 250);
      return;
    }
    el('mm-trq-full').checked = mmTrWant.full;
    el('mm-trq-summary').checked = mmTrWant.summary;
    mmTrSyncOk();
    dlg.hidden = false;
  }

  function mmTrSyncOk() {
    const ok = el('mm-trq-start');
    if (!ok) return;
    // Neither box ticked means recording for no reason; say so rather than
    // letting someone talk for half an hour and keep nothing.
    const any = el('mm-trq-full').checked || el('mm-trq-summary').checked;
    ok.disabled = !any;
    const note = el('mm-trq-none');
    if (note) note.hidden = any;
  }

  function mmTrConfirm() {
    mmTrWant = { full: el('mm-trq-full').checked, summary: el('mm-trq-summary').checked };
    el('mm-trq-dlg').hidden = true;
    mmOpenEditor(null);
    setTimeout(() => mmStartMic().catch(e => console.warn('mic:', e)), 250);
  }

  /* Tidy / Summarize, with a one-step undo so nothing is ever lost. */
  let mmTextUndo = null;
  function mmShowUndo(show) { const b = el('mm-undo-btn'); if (b) b.hidden = !show; }
  function mmApplyToBody(fn, emptyMsg, doneMsg) {
    const body = el('mm-note-body'); if (!body) return;
    const before = body.value || '';
    if (!before.trim()) { mmToast(emptyMsg, true); return; }
    const after = fn(before);
    if (!after || after === before) { mmToast('Nothing to change', true); return; }
    mmTextUndo = before;
    body.value = after;
    mmShowUndo(true);
    mmToast(doneMsg);
  }
  /* Tidies the transcript itself, in place. The old buttons tidied the NOTE,
     which was the wrong target — a note somebody typed is already readable,
     and the unpunctuated wall of filler is over in the transcript. Free,
     instant, works with no signal. */
  function mmTidyTranscript() {
    const tr = el('mm-transcript-text');
    if (!tr) return;
    const raw = (tr.value || '').trim();
    if (!raw) { mmToast('Nothing in the transcript yet', true); return; }
    const shaped = mmFormatTranscript(raw);
    if (!shaped || shaped === raw) { mmToast('Nothing to change', true); return; }
    tr.value = shaped;
    mmToast('Transcript tidied ✓');
  }

  function mmTidyText() {
    mmApplyToBody(t => mmFormatTranscript(t), 'Nothing to tidy yet', 'Tidied up ✓');
    // If the note still has no title, offer one drawn from the transcript.
    const titleEl = el('mm-note-title'), bodyEl = el('mm-note-body');
    if (titleEl && bodyEl && !titleEl.value.trim()) {
      const suggested = mmSuggestTitle(bodyEl.value);
      if (suggested) titleEl.value = suggested;
    }
  }
  /* "Tidy with AI" — the same job as mmTidyText, done by a model instead of
     regular expressions.

     Kept as a SEPARATE button rather than replacing the existing one. The
     pattern-matching version is instant, free, and works with no signal; this
     one is better on messy dictation but costs a fraction of a cent, needs a
     connection, and takes a second or two. On a doorstep with one bar of
     service, the offline one is the right tool, so both stay.

     It writes through mmTextUndo like everything else here, so Undo restores
     the original in one click and nothing is ever lost. */
  /* Move transcript text into the note (tidied or summarised), then clear it
     so the next stretch of dictation starts fresh. */
  function mmTranscriptToNote(mode) {
    const tr = el('mm-transcript-text'), body = el('mm-note-body');
    if (!tr || !body) return;
    const raw = (tr.value || '').trim();
    if (!raw) { mmToast('Nothing in the transcript yet', true); return; }
    // Called from the drawer while browsing the room — open a note to land in.
    if (mmView !== 'editor') { mmOpenEditor(null); }
    const piece = (mode === 'summary') ? mmSummarize(raw) : mmFormatTranscript(raw);
    if (!piece) { mmToast('Nothing to add', true); return; }
    mmTextUndo = body.value;                       // one-step undo covers this too
    body.value = (body.value.trim() ? body.value.replace(/\s*$/, '') + '\n\n' : '') + piece;
    mmShowUndo(true);
    tr.value = '';
    const titleEl = el('mm-note-title');
    if (titleEl && !titleEl.value.trim()) {
      const suggested = mmSuggestTitle(raw);
      if (suggested) titleEl.value = suggested;
    }
    body.scrollTop = body.scrollHeight;
    mmToast(mode === 'summary' ? 'Summary added to note ✓' : 'Added to note ✓');
  }

  function mmUndoText() {
    const body = el('mm-note-body');
    if (!body || mmTextUndo === null) return;
    body.value = mmTextUndo;
    mmTextUndo = null;
    mmShowUndo(false);
    mmToast('Reverted');
  }

  // ---------- attach-client chip + autocomplete ----------
  function mmRenderChips() {
    const wrap = el('mm-chips');
    if (!wrap) return;
    if (mmClient) {
      wrap.innerHTML = `<span class="mm-chip">&#9670; ${escHtml(mmClient)} <button id="mm-chip-x" title="Remove">&times;</button></span>` + `<span class="mm-attach" id="mm-attach-toggle">change</span>`;
      const x = el('mm-chip-x'); if (x) x.addEventListener('click', () => { mmClient = null; mmRenderChips(); });
    } else {
      wrap.innerHTML = `<span class="mm-attach" id="mm-attach-toggle">+ attach client</span>`;
    }
    const tog = el('mm-attach-toggle'); if (tog) tog.addEventListener('click', mmShowAttachInput);
  }
  function mmSugBox() { let box = el('mm-sug-box'); if (box) return box; box = document.createElement('div'); box.id = 'mm-sug-box'; box.className = 'mm-sug-box'; document.body.appendChild(box); return box; }
  function mmHideSug() { const b = el('mm-sug-box'); if (b) b.style.display = 'none'; }
  function mmClientPool(q) {
    const uid = mmUid();
    let clients = [], refs = [];
    try {
      if (mmTeam()) {
        const seeAll = (typeof canSeeAllInSection === 'function') ? canSeeAllInSection('Clients') : true;
        clients = (typeof teamClientsCache !== 'undefined' ? teamClientsCache : []).filter(c => seeAll || clientIsAssigned(c, uid));
        refs = (typeof teamReferralsCache !== 'undefined' && Array.isArray(teamReferralsCache)) ? teamReferralsCache : [];
      } else {
        clients = (typeof loadClients === 'function') ? loadClients() : [];
        refs = (typeof loadReferrals === 'function') ? loadReferrals() : [];
      }
    } catch (e) {}
    const pool = [...clients.map(c => ({ name: c.name || '', kind: 'client' })), ...refs.map(r => ({ name: r.name || '', kind: 'referral' }))].filter(p => p.name && p.name.toLowerCase().includes(q));
    const seen = new Set();
    return pool.filter(p => !seen.has(p.name.toLowerCase()) && seen.add(p.name.toLowerCase())).slice(0, 8);
  }
  function mmShowAttachInput() {
    const wrap = el('mm-chips'); if (!wrap) return;
    const prefix = mmClient ? `<span class="mm-chip">&#9670; ${escHtml(mmClient)} <button id="mm-chip-x" title="Remove">&times;</button></span>` : '';
    wrap.innerHTML = prefix + `<input class="mm-attach-input" id="mm-attach-input" type="text" placeholder="Search clients&hellip;" autocomplete="off" />`;
    const x = el('mm-chip-x'); if (x) x.addEventListener('click', () => { mmClient = null; mmRenderChips(); });
    const inp = el('mm-attach-input'); if (!inp) return;
    inp.focus();
    inp.addEventListener('input', () => mmShowSug(inp));
    inp.addEventListener('blur', () => setTimeout(() => { mmHideSug(); mmRenderChips(); }, 150));
    inp.addEventListener('keydown', e => {
      if (e.key === 'Enter') { e.preventDefault(); if (inp.value.trim()) { mmClient = inp.value.trim(); mmHideSug(); mmRenderChips(); } }
      else if (e.key === 'Escape') { mmHideSug(); mmRenderChips(); }
    });
  }
  function mmShowSug(input) {
    const q = (input.value || '').trim().toLowerCase();
    const box = mmSugBox();
    if (q.length < 1) { box.style.display = 'none'; return; }
    const matches = mmClientPool(q);
    if (!matches.length) { box.style.display = 'none'; return; }
    const r = input.getBoundingClientRect();
    box.style.left = r.left + 'px'; box.style.top = (r.bottom + 4) + 'px'; box.style.minWidth = Math.max(r.width, 200) + 'px';
    box.innerHTML = matches.map(m => `<div class="row" data-name="${escHtml(m.name)}"><b>${escHtml(m.name)}</b><span class="kind">${m.kind}</span></div>`).join('');
    box.style.display = 'block';
    box.querySelectorAll('.row').forEach(row => { row.addEventListener('mousedown', e => { e.preventDefault(); mmClient = row.dataset.name; box.style.display = 'none'; mmRenderChips(); }); });
  }

  // ---------- bubbly calendar ----------
  function mmMonthMatrix(anchor) {
    const y = anchor.getFullYear(), m = anchor.getMonth();
    const startDow = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }
  function mmEventsByDay() {
    const map = {}; let evs = [];
    try { evs = (typeof calViewEvents === 'function') ? calViewEvents() : ((typeof calAllEvents === 'function') ? calAllEvents() : []); } catch (e) {}
    (evs || []).forEach(e => { if (e && e.date && e.type !== '__topic__') { (map[e.date] = map[e.date] || []).push(e); } });
    return map;
  }
  function mmRenderCalendar() {
    const wrap = el('mm-cal'); if (!wrap) return;
    const anchor = mmCalAnchor || new Date();
    const monthLabel = anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    const todayKey = calTodayKeySafe();
    const evMap = mmEventsByDay();
    const cells = mmMonthMatrix(anchor);
    const heads = ['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(h => `<span class="mm-cal-head">${h}</span>`).join('');
    const daysHtml = cells.map(d => {
      if (!d) return '<span class="mm-cal-empty"></span>';
      const key = calDateKeySafe(d);
      const evs = evMap[key] || [];
      const dot = evs.length ? `<span class="mm-cal-dot" style="background:${evs[0].color || '#7fbce0'}"></span>` : '';
      const cls = ['mm-cal-day']; if (key === todayKey) cls.push('today'); if (key === mmCalSelected) cls.push('sel');
      return `<button class="${cls.join(' ')}" data-key="${key}">${d.getDate()}${dot}</button>`;
    }).join('');
    const selKey = mmCalSelected || todayKey;
    const selLabel = new Date(selKey + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
    const dayEvents = (evMap[selKey] || []).slice().sort((a, b) => (a.time || '').localeCompare(b.time || ''));
    const agendaRows = dayEvents.length
      ? dayEvents.map(ev => {
          const color = ev.color || (ev.type === 'event' ? '#6366f1' : '#7fbce0');
          let meta = ev.type === 'event' ? (ev.time || 'Event') : (ev.done ? 'Done' : 'Follow-up');
          const strike = ev.done ? 'text-decoration:line-through; opacity:.7;' : '';
          return `<div class="mm-cal-ev" data-id="${escHtml(ev.id)}">
              <span class="mm-cal-ev-dot" style="background:${color}"></span>
              <span class="mm-cal-ev-title" style="${strike}">${escHtml(ev.title || 'Untitled')}</span>
              <span class="mm-cal-ev-meta">${escHtml(meta)}</span>
            </div>`;
        }).join('')
      : `<div class="mm-cal-empty-note">Nothing on this day yet.</div>`;
    const filterRow = mmTeam()
      ? `<div class="mm-cal-filter" id="mm-cal-filter"><button data-calfilter="all" class="${calViewFilter === 'all' ? 'active' : ''}">All</button><button data-calfilter="mine" class="${calViewFilter === 'mine' ? 'active' : ''}">Mine</button></div>`
      : '';
    wrap.innerHTML =
      `${filterRow}
       <div class="mm-cal-top">
         <button class="mm-cal-nav" id="mm-cal-prev">&lsaquo;</button>
         <span class="mm-cal-month">${escHtml(monthLabel)}</span>
         <button class="mm-cal-nav" id="mm-cal-next">&rsaquo;</button>
       </div>
       <div class="mm-cal-grid">${heads}${daysHtml}</div>
       <div class="mm-cal-agenda">
         <div class="mm-cal-agenda-head">
           <span class="mm-cal-agenda-day">${escHtml(selLabel)}</span>
           <button class="mm-btn mm-btn-primary mm-cal-addbtn" id="mm-cal-addbtn">+ Add to this day</button>
         </div>
         <div class="mm-cal-list">${agendaRows}</div>
       </div>`;
    document.querySelectorAll('#mm-cal-filter button').forEach(b => b.addEventListener('click', () => {
      calViewFilter = b.dataset.calfilter;
      if (typeof calSyncFilterToggles === 'function') calSyncFilterToggles();
      mmRenderCalendar();
    }));
    el('mm-cal-prev').addEventListener('click', () => { mmCalAnchor = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1); mmRenderCalendar(); });
    el('mm-cal-next').addEventListener('click', () => { mmCalAnchor = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1); mmRenderCalendar(); });
    wrap.querySelectorAll('.mm-cal-day').forEach(b => b.addEventListener('click', () => { mmCalSelected = b.dataset.key; mmRenderCalendar(); }));
    wrap.querySelectorAll('.mm-cal-ev').forEach(row => row.addEventListener('click', () => mmEditCalItem(row.dataset.id)));
    el('mm-cal-addbtn').addEventListener('click', mmOpenFullAdd);
  }
  function mmOpenFullAdd() {
    if (typeof calOpenAddModal !== 'function') { mmToast('Calendar unavailable', true); return; }
    try { if (typeof calSelected !== 'undefined') calSelected = mmCalSelected || calTodayKeySafe(); } catch (e) {}
    calOpenAddModal();
    setTimeout(() => { const t = el('cal-ev-title'); if (t && mmLastClient && !t.value) { t.value = mmLastClient + ' — follow up'; t.focus(); } }, 70);
  }
  function mmEditCalItem(id) { if (!id) return; if (typeof calEditEvent === 'function') calEditEvent(id); }
  function mmWatchCalModal() {
    const bd = el('cal-event-backdrop');
    if (!bd || bd._mmWatched) return;
    bd._mmWatched = true;
    let wasOpen = bd.classList.contains('open');
    new MutationObserver(() => {
      const isOpen = bd.classList.contains('open');
      if (wasOpen && !isOpen && overlay() && overlay().classList.contains('open') && mmView === 'calendar') mmRenderCalendar();
      wasOpen = isOpen;
    }).observe(bd, { attributes: true, attributeFilter: ['class'] });
  }

  // ---------- view switching / toast ----------
  function mmSetView(view) {
    // Transcription spans the whole of Meeting Mode now, so switching views
    // leaves both the mic and the drawer alone. Closing Meeting Mode stops it.
    mmView = view;
    document.querySelectorAll('#meeting-mode .mm-view').forEach(v => v.classList.remove('active'));
    const map = { lobby: 'mm-view-lobby', room: 'mm-view-room', editor: 'mm-view-editor', calendar: 'mm-view-calendar', items: 'mm-view-items' };
    const a = el(map[view]); if (a) a.classList.add('active');
    /* The transcribe control and its language picker are hidden in the lobby.
       There is nothing to transcribe on a screen whose only job is choosing a
       room, and a live-looking control that does nothing teaches people to
       ignore it. The title bar spans every view, so this has to be switched
       here rather than in CSS — the views are its siblings, not its children. */
    const tools = document.querySelector('#meeting-mode .mm-title-tools');
    if (tools) tools.hidden = (view === 'lobby');
    const title = el('mm-title'), crumb = el('mm-crumb-live');
    if (view === 'lobby') { if (title) title.textContent = 'Meeting Mode'; if (crumb) crumb.innerHTML = '<span class="mm-live-dot"></span>Choose a room'; mmRenderLobby(); mmStartLobbyRefresh(); }
    else { mmStopLobbyRefresh(); }
    if (view === 'room') mmRenderRoom();
    if (view === 'editor') { if (title) title.textContent = 'Call Notes'; if (crumb) crumb.innerHTML = '<span class="mm-live-dot"></span>' + escHtml(mmRoomName || 'On a call'); }
    if (view === 'calendar') { if (title) title.textContent = 'Calendar'; if (crumb) crumb.textContent = 'Calendar'; mmRenderCalendar(); }
    if (view === 'items') { if (title) title.textContent = 'My items'; if (crumb) crumb.textContent = 'My items'; mmRenderItems(); }
  }

  let mmToastTimer = null;
  function mmToast(text, isWarn) {
    const t = el('mm-toast'); if (!t) return;
    t.textContent = text;
    t.style.background = isWarn ? 'rgba(100,116,139,.94)' : 'rgba(16,185,129,.92)';
    t.classList.add('show');
    if (mmToastTimer) clearTimeout(mmToastTimer);
    mmToastTimer = setTimeout(() => t.classList.remove('show'), 1900);
  }

  // ---------- presence (People tab "In Meeting Mode") ----------
  async function mmSetPresence(active, roomId, roomName) {
    try {
      if (mmTeam() && activeTeamId && currentUser && fbDb) {
        const patch = { meetingActive: !!active, meetingSince: active ? new Date().toISOString() : '', meetingRoomId: active ? (roomId || '') : '', meetingRoomName: active ? (roomName || '') : '' };
        const me = (typeof teamMembersCache !== 'undefined' ? teamMembersCache : []).find(m => m.uid === currentUser.uid);
        if (me) { Object.assign(me, patch); if (typeof renderTeamPeople === 'function') { try { renderTeamPeople(); } catch (e) {} } }
        await fbDb.collection('teams').doc(activeTeamId).collection('members').doc(currentUser.uid).update(patch);
      }
    } catch (e) {
      // Was an empty catch, which made a failed presence write look identical
      // to "nobody is in the room" — the exact symptom that is hardest to
      // diagnose. Never silent again.
      console.warn('Meeting presence write failed:', (e && e.code) || e);
    }
  }

  // ---------- open / close / back ----------
  function mmOpen() {
    const o = overlay(); if (!o) return;
    // Prefer the pre-warmed image; if warming hasn't finished, open on the
    // gradient alone and let the photo fade in when it arrives rather than
    // holding the overlay back.
    const bg = mmNextBg;
    o.style.backgroundImage = bg ? `url('${bg}'), ${MM_BG_FALLBACK}` : MM_BG_FALLBACK;
    if (bg) mmLastBg = bg;
    if (!bg) {
      const pick = mmPickBg();
      mmLastBg = pick;
      const img = new Image();
      img.onload = () => {
        if (o.classList.contains('open')) o.style.backgroundImage = `url('${pick}'), ${MM_BG_FALLBACK}`;
      };
      img.src = pick;
    }
    // Line up a fresh one for next time.
    mmNextBg = null;
    mmWarmNextBg();
    mmEditingId = null; mmClient = null; mmSessionNoteIds = [];
    mmRoomId = null; mmRoomPending = false; mmRoomMembers = []; mmRoomNotes = []; mmRoomRequests = []; mmRoomViewUid = mmUid();
    el('mm-note-title').value = ''; el('mm-note-body').value = '';
    const d = new Date(); const sub = el('mm-sub');
    if (sub) sub.textContent = d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' }) + ' · ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    mmCalAnchor = new Date(); mmCalSelected = calTodayKeySafe();
    o.classList.add('open'); o.setAttribute('aria-hidden', 'false');
    mmSetView('lobby');
    mmStartMyReqListener();
  }
  /* When the host leaves a room with notes in it, archive the whole meeting into a
     named folder in the team's Notes. Public notes are shared with everyone who was
     in the room; private notes stay with their author only. Then the room is cleared. */
  /* Delete a note from the ROOM.

     Room notes live in rooms/{roomId}/notes, which is not the same collection
     as the Notes board at teams/{id}/notes. "Add to Notes" COPIES a note to the
     board; deleting it there leaves the room original untouched. Before this
     existed there was simply no way to remove a note from a room. */
  async function mmDeleteRoomNote(id) {
    const n = (mmRoomNotes || []).find(x => x.id === id);
    if (!n) return;
    const copied = n.addedToNotes;
    const msg = copied
      ? 'Remove this note from the meeting room?\n\nThe copy already saved in your Notes section is not affected.'
      : 'Delete this note?\n\nIt has not been added to your Notes section, so it will be gone for good.';
    if (!confirm(msg)) return;
    try {
      await mmRoomRef(mmRoomId).collection('notes').doc(id).delete();
      mmRoomNotes = mmRoomNotes.filter(x => x.id !== id);
      mmRenderRoom();
      mmToast('Note removed');
    } catch (e) {
      mmToast('Could not delete the note', true);
      console.warn('Room note delete failed:', e && (e.code || e.message));
    }
  }

  async function mmMaybeArchiveMeeting() {
    if (!mmTeam() || !mmRoomId) return;
    /* Who is responsible for tidying the room?

       - A private room: its host.
       - The TEAM room: whoever turns the lights off, i.e. the last person with
         presence still marked active. Previously this function returned early
         for the team room entirely, so notes there accumulated forever with no
         way to clear them. */
    const me = mmUid();
    const othersStillHere = (mmRoomMembers || []).filter(m => m.active && m.uid !== me).length;
    const iAmHost = (mmRoomType === 'private' && mmRoomOwnerUid === me);
    const iAmLastOut = (mmRoomType !== 'private' && othersStillHere === 0);
    if (!iAmHost && !iAmLastOut) return;
    const notes = mmRoomNotes.slice();
    if (!notes.length) return;
    const lead = iAmLastOut
      ? `You are the last one leaving the team room.\n\nSave its ${notes.length} note${notes.length === 1 ? '' : 's'} into a folder and clear the room?`
      : `End this meeting and save its ${notes.length} note${notes.length === 1 ? '' : 's'} into a folder?`;
    const ok = confirm(lead + `\n\nEveryone who was here keeps the public notes; private notes stay with their author.`);
    if (!ok) return;
    /* Folder nesting is encoded as a path with "/" (see notesFolderDepth), so
       a name containing slashes silently becomes nested folders. The old
       default was 'Meeting ' + toLocaleDateString(), i.e. "Meeting 8/11/2026",
       which the app dutifully turned into Meeting 8 > 11 > 2026 — and the user
       saw a stray folder called "2026".

       Now: a real "Meeting" parent with a slash-free dated child inside it. */
    const stamp = new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                    .replace(/\//g, '-');
    /* No prompt, and no folder is created.

       Asking someone to name a folder as they leave a call was a question at
       the worst possible moment, and it grew a dated tree underneath a Notes
       section that had just finished demoting folders to a filter. Each
       document already carries meetingAt, meetingRoom and fromMeeting — the
       Meetings filter reads those, so the folder duplicated metadata that was
       already there.

       Existing folders are untouched; meeting documents arrive unfiled and can
       be filed by hand like anything else. */
    const label = (mmRoomName ? mmRoomName + ' — ' : 'Meeting — ') + stamp;
    const folder = '';
    const participants = Array.from(new Set((mmRoomMembers || []).map(m => m.uid))).filter(Boolean);
    const now = new Date().toISOString();
    try {
      for (const n of notes) {
        const others = participants.filter(u => u !== n.authorUid);
        // Client-attached notes go to the client's folder; others to the meeting folder.
        const r = (typeof notesResolveAttached === 'function') ? notesResolveAttached(n.clientsJson, folder) : { clientsJson: n.clientsJson || '[]', folder };
        /* A note marked PUBLIC in the room becomes public to the whole team.

           It used to be shared with exactly the uids present in the room at the
           moment of archiving. That failed quietly and often: anyone who had
           already left, or whose presence had not synced, was left off the
           list, and Firestore then refused them the read. The symptom was a
           meeting folder that looked empty on one device and full on another.

           "Public" in the room now means public in the team, which is what
           people mean when they press the button. Private notes are unchanged —
           they stay with their author and nobody else can read them. */
        const base = {
          /* Documents, not notes. These are typed text, so they should open in
             the document editor with its formatting toolbar rather than the
             plain box — and badge as what they are in the grid. */
          title: n.title || label, body: n.body || '', kind: 'document', folder: r.folder || folder,
          clientsJson: r.clientsJson,
          clientAssignedUids: (typeof notesClientAccessUids === 'function' ? notesClientAccessUids(r.clientsJson) : []),
          color: n.color || '',
          shareAll: !!n.public,
          sharedWithJson: '[]',
          sharedWith: [],
          createdAt: n.createdAt || now, updatedAt: now,
          addedBy: n.authorUid, addedByName: n.authorName || 'Member',
          fromMeeting: label,
          // Session metadata: several meetings can land in one day's folder, so
          // each note records which sitting it came from. This is what a
          // calendar view groups on.
          meetingAt: now,
          meetingRoom: mmRoomName || ''
        };
        const ref = await fbDb.collection('teams').doc(activeTeamId).collection('notes').add(base);
        if (typeof teamNotesCache !== 'undefined' && !teamNotesCache.find(x => x.id === ref.id)) teamNotesCache.push({ id: ref.id, ...base });
      }
      /* The transcript is filed as its own document, and it is PRIVATE to
         whoever ran the meeting.

         A transcript is a record of what people said, which is not the same as
         notes. People say things on a call they would never write down — a
         half-formed thought, a name in passing, something about a colleague.
         Publishing that verbatim before anyone has read it back changes what is
         safe to say on a call, and a team that notices will start
         self-censoring. So it lands private with a Share button; one click
         publishes it, and that click is a person deciding. */
      const trBox = el('mm-transcript-text');
      const transcript = trBox ? (trBox.value || '').trim() : '';

      /* The AI summary, if it was asked for before recording started. Written
         first, because it is the one people actually read — and if the model
         call fails we still want the verbatim record filed rather than losing
         both. */
      if (transcript && mmTrWant.summary) {
        try {
          const summary = (await window.otterAI('summarizeTranscript', transcript) || '').trim();
          if (summary) {
            const sDoc = {
              title: (mmRoomName ? mmRoomName + ' — ' : '') + 'Summary · ' + stamp,
              body: summary, kind: 'document', folder,
              clientsJson: '[]', clientAssignedUids: [], color: '',
              shareAll: false, sharedWithJson: '[]', sharedWith: [],
              source: 'summary',
              createdAt: now, updatedAt: now,
              addedBy: mmUid(), addedByName: mmMyName(),
              fromMeeting: label, meetingAt: now, meetingRoom: mmRoomName || ''
            };
            const sRef = await fbDb.collection('teams').doc(activeTeamId).collection('notes').add(sDoc);
            if (typeof teamNotesCache !== 'undefined' && !teamNotesCache.find(x => x.id === sRef.id)) {
              teamNotesCache.push({ id: sRef.id, ...sDoc });
            }
          }
        } catch (e) {
          console.warn('meeting summary failed:', e && e.message);
          mmToast('Could not write the summary — the transcript is still saved', true);
        }
      }

      if (transcript && mmTrWant.full) {
        const tDoc = {
          title: (mmRoomName ? mmRoomName + ' — ' : '') + 'Transcript · ' + stamp,
          body: transcript,
          kind: 'document', folder,
          clientsJson: '[]', clientAssignedUids: [], color: '',
          shareAll: false, sharedWithJson: '[]', sharedWith: [],
          source: 'transcript',
          createdAt: now, updatedAt: now,
          addedBy: mmUid(), addedByName: mmMyName(),
          fromMeeting: label, meetingAt: now, meetingRoom: mmRoomName || ''
        };
        const tRef = await fbDb.collection('teams').doc(activeTeamId).collection('notes').add(tDoc);
        if (typeof teamNotesCache !== 'undefined' && !teamNotesCache.find(x => x.id === tRef.id)) {
          teamNotesCache.push({ id: tRef.id, ...tDoc });
        }
      }
      if (trBox) trBox.value = '';

      for (const n of notes) { try { await mmRoomRef(mmRoomId).collection('notes').doc(n.id).delete(); } catch (e) {} }
      mmRoomNotes = [];
      if (typeof renderNotes === 'function') { try { renderNotes(); } catch (e) {} }
      const kept = [mmTrWant.full && 'transcript', mmTrWant.summary && 'summary'].filter(Boolean);
      mmToast(transcript && kept.length
        ? 'Saved to Documents ✓ — ' + kept.join(' and ') + ' filed privately'
        : 'Saved to Documents ✓');
    } catch (e) { mmToast('Could not archive meeting', true); }
  }
  async function mmExitRoom() {
    // Leaving the room must also leave the CALL. LiveKit bills for connected
    // time, not for whether a camera is on, so a call left running in a
    // background tab quietly meters until the tab is closed.
    if (typeof mmCallEnd === 'function') mmCallEnd();
    await mmMaybeArchiveMeeting();
    await mmLeaveRoom();
    mmSetView('lobby');
  }
  async function mmClose() {
    const o = overlay(); if (!o) return;
    if (typeof mmCallEnd === 'function') mmCallEnd();   // never leave a call running behind a closed overlay
    if (typeof mmStopMic === 'function') mmStopMic();
    const trBox = el('mm-transcript-text'); if (trBox) trBox.value = '';
    if (typeof mmShowTranscript === 'function') mmShowTranscript(false);
    // Host archives the meeting to a folder on the way out.
    if (mmRoomId && mmTeam()) await mmMaybeArchiveMeeting();
    // Personal (no team) mode keeps the simple "add my notes to a folder" prompt.
    if (!mmTeam()) {
      const made = mmSessionNoteIds.filter(id => mmAllNotes().some(n => n.id === id && !n.addedToNotes));
      if (made.length) {
        const ok = confirm(`You made ${made.length} note${made.length === 1 ? '' : 's'} in Meeting Mode.\n\nAdd ${made.length === 1 ? 'it' : 'them all'} to your Notes section?`);
        if (ok) {
          const name = prompt('Put them in a folder? Enter a name, or leave blank for Unfiled:', 'Call notes ' + new Date().toLocaleDateString());
          const folder = (typeof notesCreateFolder === 'function' && name && name.trim()) ? notesCreateFolder(name) : '';
          for (const id of made) { const n = mmAllNotes().find(x => x.id === id); if (n) { try { await mmCopyNoteToNotes(n, folder); } catch (e) {} } }
          if (typeof renderNotes === 'function') { try { renderNotes(); } catch (e) {} }
        }
      }
    }
    await mmLeaveRoom();
    mmStopMyReqListener();
    mmSessionNoteIds = [];
    o.classList.remove('open'); o.setAttribute('aria-hidden', 'true'); mmHideSug(); mmStopLobbyRefresh();
  }
  function mmBack() {
    if (mmView === 'editor') {
      const body = (el('mm-note-body').value || '').trim();
      const title = (el('mm-note-title').value || '').trim();
      const trEl = el('mm-transcript-text');
      const pending = trEl ? (trEl.value || '').trim() : '';
      // Include un-transferred transcript text so backing out never loses it.
      if (body || title || pending) mmSaveEditor({ silent: true });
      mmSetView('room');
      return;
    }
    if (mmView === 'calendar') { mmSetView('room'); return; }
    if (mmView === 'items') { mmSetView('room'); return; }
    if (mmView === 'room') { mmExitRoom(); return; }
    mmClose();
  }

  // ---------- wiring ----------
  function wire() {
    const navBtn = el('nav-meeting-btn'); if (navBtn) navBtn.addEventListener('click', mmOpen);
    const back = el('mm-back-btn'); if (back) back.addEventListener('click', mmBack);
    const save = el('mm-save-btn'); if (save) save.addEventListener('click', () => mmSaveEditor());
    const calBtn = el('mm-cal-btn'); if (calBtn) calBtn.addEventListener('click', mmEditorToCalendar);
    const edBack = el('mm-editor-back-btn'); if (edBack) edBack.addEventListener('click', mmBack);
    const visBtn = el('mm-editor-visbtn'); if (visBtn) visBtn.addEventListener('click', mmToggleEditVis);
    const micBtn = el('mm-mic-btn');
    if (micBtn) {
      if (!mmMicSupported()) { micBtn.style.display = 'none'; const ls = el('mm-mic-lang'); if (ls) ls.style.display = 'none'; }
      else micBtn.addEventListener('click', mmToggleMic);
    }
    const trIns = el('mm-tr-insert'); if (trIns) trIns.addEventListener('click', () => mmTranscriptToNote('insert'));

    const trClr = el('mm-tr-clear'); if (trClr) trClr.addEventListener('click', () => {
      const tr = el('mm-transcript-text');
      if (!tr || !tr.value.trim()) return;
      if (confirm('Clear the transcript? Anything not added to your note will be lost.')) tr.value = '';
    });
    // Hiding the drawer keeps the transcript AND keeps listening — it just
    // gets out of the way. The header chip brings it back.
    const trX = el('mm-transcript-close'); if (trX) trX.addEventListener('click', () => mmShowTranscript(false));
    const trShow = el('mm-transcript-show'); if (trShow) trShow.addEventListener('click', () => mmShowTranscript(true));
    const trMin = el('mm-transcript-min');
    if (trMin) trMin.addEventListener('click', () => {
      const pane = el('mm-transcript-pane');
      if (!pane) return;
      const on = !pane.classList.contains('collapsed');
      pane.classList.toggle('collapsed', on);
      trMin.innerHTML = on ? '&plus;' : '&minus;';
      trMin.title = on ? 'Expand' : 'Collapse';
      // Collapsed, the drawer is a strip — the note can have the width back.
      const view = el('mm-view-editor');
      if (view) view.classList.toggle('with-transcript', !on);
    });
    const trTidy = el('mm-tr-tidy'); if (trTidy) trTidy.addEventListener('click', mmTidyTranscript);
    // Same tidy, different target: this one cleans the note the CHW is writing.
    const tidyBtn = el('mm-tidy-btn'); if (tidyBtn) tidyBtn.addEventListener('click', mmTidyText);
    const undoBtn = el('mm-undo-btn'); if (undoBtn) undoBtn.addEventListener('click', mmUndoText);
    const dictBtn = el('mm-dictate-btn');
    if (dictBtn) {
      if (!mmMicSupported()) dictBtn.style.display = 'none';
      else dictBtn.addEventListener('click', mmAskTranscribe);
    }
    const trFull = el('mm-trq-full'); if (trFull) trFull.addEventListener('change', mmTrSyncOk);
    const trSumBox = el('mm-trq-summary'); if (trSumBox) trSumBox.addEventListener('change', mmTrSyncOk);
    const trGo = el('mm-trq-start'); if (trGo) trGo.addEventListener('click', mmTrConfirm);
    const trNo = el('mm-trq-cancel'); if (trNo) trNo.addEventListener('click', () => { el('mm-trq-dlg').hidden = true; });
    const itemsBtn = el('mm-items-btn'); if (itemsBtn) itemsBtn.addEventListener('click', () => mmSetView('items'));
    const itemsBack = el('mm-items-back-btn'); if (itemsBack) itemsBack.addEventListener('click', mmBack);
    const calBack = el('mm-cal-back-btn'); if (calBack) calBack.addEventListener('click', mmBack);
    const leave = el('mm-leave-btn'); if (leave) leave.addEventListener('click', () => { mmCallEnd(); mmBack(); });
    const addall = el('mm-addall-btn'); if (addall) addall.addEventListener('click', mmAddAllToNotes);
    // Video call controls
    const callBtn = el('mm-call-btn');
    if (callBtn) {
      // getUserMedia needs a secure context; hide the entry point where it
      // can't work rather than offering a button that always errors.
      if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) callBtn.style.display = 'none';
      else callBtn.addEventListener('click', () => { if (mmCall.active) mmCallEnd(); else mmCallStart(); });
    }
    const muteBtn = el('mm-mute-btn');   if (muteBtn)   muteBtn.addEventListener('click', mmToggleMute);
    const camBtn  = el('mm-cam-btn');    if (camBtn)    camBtn.addEventListener('click', mmToggleCam);
    const hangBtn = el('mm-hangup-btn'); if (hangBtn)   hangBtn.addEventListener('click', () => mmCallEnd());
    const extBtn  = el('mm-extend-btn'); if (extBtn)    extBtn.addEventListener('click', mmExtendCall);
    // A call must never outlive the overlay or the tab.
    window.addEventListener('pagehide', () => mmCallEnd());
    mmWatchCalModal();
    window.addEventListener('pagehide', () => { if (overlay() && overlay().classList.contains('open')) mmSetPresence(false); });
    /* Backgrounding a tab used to mark you as having LEFT the room.

       That makes "see who's in the room" impossible to ever observe: to look
       at your own screen you have to bring your window forward, which pushes
       everyone else's window behind — so from their side you just vanished.
       On one machine with two windows nobody is ever visible at all.

       Being in a room is not the same as looking at it. Presence now only
       clears on a real exit: leaving the room, closing Meeting Mode, or
       closing the tab. Coming back to the foreground refreshes the timestamp
       so the six-hour staleness check keeps working. */
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && overlay() && overlay().classList.contains('open') && mmRoomId) {
        mmSetPresence(true, mmRoomId, mmRoomName);
      }
    });
    document.addEventListener('keydown', e => {
      // Escape drops the spotlight before it does anything else — otherwise
      // someone enlarging a tile and hitting Escape would leave the room.
      if (e.key === 'Escape' && mmCall.focusUid) { mmCall.focusUid = null; mmRenderTiles(); return; }
      if (e.key === 'Escape' && overlay() && overlay().classList.contains('open')) { const bd = el('cal-event-backdrop'); if (bd && bd.classList.contains('open')) return; mmBack(); }
    });
    mmRenderChips();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire);
  else wire();

